import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { translateText } from '../services/translationService';

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import API_BASE_URL from '../config/api';
import { COMMON_CERTIFICATIONS, CERTIFICATIONS_BY_SCHEMA_KEY } from '../config/certifications';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import styles from './AdminPage.module.css';

// --- DATA SCHEMAS & MAPPINGS ---

const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'CHF', 'PLN', 'CZK', 'HUF', 'SEK', 'NOK', 'DKK'];

const UNIVERSAL_PROPERTIES = [
  { name: 'brand', label: 'Brand', type: 'text', required: true },
  { name: 'name', label: 'Product Name (Max 60 chars)', type: 'text', required: true, maxLength: 60 },
  { name: 'price', label: 'Price (Display)', type: 'text', required: true },
  { name: 'currency', label: 'Currency', type: 'select', options: CURRENCY_OPTIONS, required: true },
  { name: 'weight', label: 'Weight (g)', type: 'text', required: true },
  { name: 'gender', label: 'Gender', type: 'select', options: ['Men\'s', 'Women\'s', 'Unisex'], required: false },
  { name: 'material_composition', label: 'Material Composition', type: 'text', required: true },
  { name: 'certifications', label: 'Certifications', type: 'tag-multiselect', required: false },
  { name: 'description', label: 'Description', type: 'textarea', required: false }
];

const CATEGORY_SPECIFIC_PROPERTIES = {
  footwear: [
    { name: 'boot_category', label: 'Boot Category', type: 'select', options: ['B0', 'B1', 'B2', 'B3'], required: false },
    { name: 'crampon_compat', label: 'Crampon Compatibility', type: 'select', options: ['None', 'C1', 'C2', 'C3'], required: false },
    { name: 'waterproofing', label: 'Waterproofing (Membrane)', type: 'text', required: false },
    { name: 'sole_type', label: 'Sole Type', type: 'text', required: true },
    { name: 'upper_material', label: 'Upper Material', type: 'select', options: ['Leather', 'Synthetic', 'Hybrid'], required: true }
  ],
  hardshell_jacket: [
    { name: 'waterproofness', label: 'Waterproofness (mm)', type: 'text', required: true },
    { name: 'breathability_metric', label: 'Breathability Method', type: 'select', options: ['g/m²/24h', 'RET'], required: true },
    { name: 'breathability', label: 'Breathability Value', type: 'text', required: true },
    { name: 'construction', label: 'Construction', type: 'select', options: ['2-layer', '2.5-layer', '3-layer'], required: true },
    { name: 'helmet_compatible', label: 'Helmet Compatible', type: 'checkbox', required: false }
  ],
  hardshell_pants: [
    { name: 'waterproofness', label: 'Waterproofness (mm)', type: 'text', required: true },
    { name: 'breathability_metric', label: 'Breathability Method', type: 'select', options: ['g/m²/24h', 'RET'], required: true },
    { name: 'breathability', label: 'Breathability Value', type: 'text', required: true },
    { name: 'construction', label: 'Construction', type: 'select', options: ['2-layer', '2.5-layer', '3-layer'], required: true }
  ],
  softshell_jacket: [
    { name: 'water_resistance', label: 'Water Resistant', type: 'checkbox', required: false },
    { name: 'construction', label: 'Construction', type: 'select', options: ['Softshell', 'Hybrid', 'Other'], required: false },
    { name: 'helmet_compatible', label: 'Helmet Compatible', type: 'checkbox', required: false }
  ],
  softshell_pants: [
    { name: 'water_resistance', label: 'Water Resistant', type: 'checkbox', required: false },
    { name: 'construction', label: 'Construction', type: 'select', options: ['Softshell', 'Hybrid', 'Other'], required: false }
  ],
  fleece: [
    { name: 'material_type', label: 'Material Type', type: 'select', options: ['Fleece', 'Merino', 'Synthetic', 'Hybrid'], required: true },
    { name: 'fabric_weight', label: 'Fabric Weight (g/m²)', type: 'text', required: false }
  ],
  down_insulation: [
    { name: 'material_type', label: 'Material Type', type: 'select', options: ['Down'], required: true },
    { name: 'fill_power', label: 'Fill Power', type: 'text', required: true },
    { name: 'fill_weight', label: 'Fill Weight (g)', type: 'text', required: true },
    { name: 'rds_certified', label: 'RDS Certified', type: 'checkbox', required: true }
  ],
  synthetic_insulation: [
    { name: 'material_type', label: 'Material Type', type: 'select', options: ['Synthetic', 'Hybrid', 'Waterproof Synthetic'], required: true },
    { name: 'fill_weight', label: 'Insulation Weight (g)', type: 'text', required: false }
  ],
  baselayer_merino: [
    { name: 'baselayer_type', label: 'Baselayer Type', type: 'select', options: ['Top', 'Bottom', 'Underwear'], required: false },
    { name: 'baselayer_subtype', label: 'Baselayer Style', type: 'select', options: ['Short Sleeve', 'Long Sleeve', 'Long', 'Short', 'Socks', 'Boxers', 'Panties', 'Bras', 'Other'], required: false },
    { name: 'fabric_weight', label: 'Fabric Weight (g/m²)', type: 'text', required: true },
    { name: 'material_base', label: 'Material Type', type: 'select', options: ['Merino', 'Blends'], required: true },
    { name: 'fiber_micron', label: 'Fiber Micron (µm)', type: 'text', required: false },
    { name: 'odor_resistance', label: 'Odor Resistance', type: 'select', options: ['Natural', 'Treated', 'None'], required: true }
  ],
  baselayer_synthetic: [
    { name: 'baselayer_type', label: 'Baselayer Type', type: 'select', options: ['Top', 'Bottom', 'Underwear'], required: false },
    { name: 'baselayer_subtype', label: 'Baselayer Style', type: 'select', options: ['Short Sleeve', 'Long Sleeve', 'Long', 'Short', 'Socks', 'Boxers', 'Panties', 'Bras', 'Other'], required: false },
    { name: 'fabric_weight', label: 'Fabric Weight (g/m²)', type: 'text', required: true },
    { name: 'material_base', label: 'Material Type', type: 'select', options: ['Synthetic', 'Hybrid', 'Mesh'], required: true },
    { name: 'odor_resistance', label: 'Odor Resistance', type: 'select', options: ['Natural', 'Treated', 'None'], required: true }
  ],
  crampons: [
    { name: 'binding_type', label: 'Binding Type', type: 'tag-multiselect', options: ['Strap-on (C1)', 'Semi-automatic (C2)', 'Automatic (C3)'], required: true },
    { name: 'material', label: 'Material', type: 'select', options: ['Steel', 'Aluminum', 'Hybrid'], required: false },
    { name: 'points', label: 'Number of Points', type: 'text', required: false },
    { name: 'front_points', label: 'Front Points', type: 'select', options: ['Dual', 'Mono', 'Horizontal', 'Vertical'], required: false },
    { name: 'antibott_included', label: 'Antibott Included', type: 'checkbox', required: false }
  ],
  ice_axes: [
    { name: 'technical_rating', label: 'Technical Rating', type: 'select', options: ['Walking', 'Ice Climbing', 'Technical'], required: false },
    { name: 'shaft_shape', label: 'Shaft Shape', type: 'select', options: ['Straight', 'Bent', 'Ergo'], required: false },
    { name: 'head_type', label: 'Head Type', type: 'select', options: ['Adze', 'Hammer'], required: false },
    { name: 'length', label: 'Length (cm)', type: 'text', required: false },
    { name: 'leash_included', label: 'Leash Included', type: 'checkbox', required: false }
  ],
  ropes: [
    { name: 'rope_type', label: 'Rope Type', type: 'select', options: ['Single', 'Half', 'Twin'], required: true },
    { name: 'diameter', label: 'Diameter (mm)', type: 'text', required: true },
    { name: 'uiaa_falls', label: 'UIAA Falls', type: 'text', required: false },
    { name: 'elongation_dynamic', label: 'Dynamic Elongation (%)', type: 'text', required: false },
    { name: 'dry_treatment', label: 'UIAA Dry Treatment', type: 'checkbox', required: false }
  ],
  carabiners: [
    { name: 'major_axis_strength', label: 'Major Axis Strength (kN)', type: 'text', required: true },
    { name: 'gate_type', label: 'Gate Type', type: 'select', options: ['Screw-lock', 'Twist-lock', 'Wire-gate', 'Solid-gate'], required: true },
    { name: 'rating', label: 'Rating (EN 12275)', type: 'text', required: false }
  ],
  backpacks: [
    { name: 'capacity', label: 'Capacity (L)', type: 'text', required: true },
    { name: 'frame_type', label: 'Frame Type', type: 'select', options: ['Internal', 'Frameless', 'External'], required: false },
    { name: 'rain_cover', label: 'Rain Cover', type: 'checkbox', required: false },
    { name: 'hip_belt', label: 'Hip Belt', type: 'select', options: ['None', 'Webbing', 'Padded', 'Padded with pocket'], required: false },
    { name: 'sternum_strap', label: 'Sternum Strap', type: 'checkbox', required: false },
    { name: 'intended_activity', label: 'Intended Activity', type: 'select', options: ['Travel', 'Hiking', 'Mountaineering', 'Alpine climbing', 'Trail running', 'Ski touring'], required: false },
    { name: 'access_style', label: 'Access Style', type: 'select', options: ['Top loading', 'Panel loading', 'Front loading', 'Roll-top', 'Clamshell', 'Hybrid'], required: false },
    { name: 'hydration_compatible', label: 'Hydration Bladder Compatibility', type: 'checkbox', required: false },
    { name: 'helmet_carry', label: 'Helmet Carry', type: 'checkbox', required: false },
    { name: 'laptop_sleeve', label: 'Laptop Sleeve', type: 'checkbox', required: false },
    { name: 'trekking_pole_carry', label: 'Trekking Pole Carry', type: 'checkbox', required: false },
    { name: 'ice_axe_carry', label: 'Ice Axe Carry', type: 'checkbox', required: false }
  ],
  accessories: [
    { name: 'material_type', label: 'Material Type', type: 'select', options: ['Synthetic', 'Merino', 'Fleece', 'Leather', 'Hybrid'], required: false }
  ],
  helmets: [
    { name: 'construction', label: 'Construction', type: 'select', options: ['Hardshell', 'In-mold', 'Hybrid'], required: true },
    { name: 'ventilation', label: 'Ventilation', type: 'select', options: ['Vented', 'Unvented'], required: false },
    { name: 'headlamp_clips', label: 'Headlamp Clips', type: 'checkbox', required: false },
    { name: 'rating', label: 'Rating/Certification', type: 'text', required: false }
  ],
  harnesses: [
    { name: 'material', label: 'Material', type: 'text', required: false },
    { name: 'gear_loops', label: 'Gear Loops', type: 'text', required: false },
    { name: 'leg_loop_type', label: 'Leg Loops', type: 'select', options: ['Adjustable', 'Fixed'], required: false },
    { name: 'ice_clipper_slots', label: 'Ice Clipper Slots', type: 'text', required: false },
    { name: 'rating', label: 'Rating/Certification', type: 'text', required: false }
  ],
  hardware: [
    { name: 'material', label: 'Material', type: 'text', required: false },
    { name: 'rating', label: 'Rating/Certification', type: 'text', required: false }
  ],
  trekking_poles: [
    { name: 'material', label: 'Material', type: 'text', required: false },
    { name: 'construction', label: 'Construction', type: 'select', options: ['Fixed', 'Adjustable', 'Folding'], required: false },
    { name: 'grip_material', label: 'Grip Material', type: 'select', options: ['Cork', 'Foam', 'Rubber', 'Plastic'], required: false },
    { name: 'locking_mechanism', label: 'Locking Mechanism', type: 'select', options: ['Lever lock', 'Twist lock', 'Push button', 'Folding'], required: false },
    { name: 'min_length', label: 'Collapsed Length (cm)', type: 'text', required: false },
    { name: 'max_length', label: 'Max Length (cm)', type: 'text', required: false }
  ],
  eyewear: [
    { name: 'material', label: 'Frame/Lens Material', type: 'text', required: false },
    { name: 'lens_category', label: 'Category', type: 'tag-multiselect', options: ['1', '2', '3', '4'], required: false }
  ],
  electronics: [
    { name: 'rating', label: 'IP/Protection Rating', type: 'text', required: false },
    { name: 'lumens', label: 'Max Lumens', type: 'text', required: false },
    { name: 'battery_type', label: 'Battery Type', type: 'select', options: ['Rechargeable', 'AAA', 'AA', 'Hybrid'], required: false },
    { name: 'red_light', label: 'Red Light Mode', type: 'checkbox', required: false }
  ]
};

// Map the granular "feed categories" to our schema keys
const CATEGORY_TO_SCHEMA_KEY = {
  'Boots': 'footwear', 'Hiking Boots': 'footwear', 'Hiking Shoes': 'footwear', 'Trail running': 'footwear',
  'Hard shell · Jackets': 'hardshell_jacket', 'Hard shell · Pants': 'hardshell_pants',
  'Soft shell · Jackets': 'softshell_jacket', 'Soft shell · Pants': 'softshell_pants',
  'Fleece': 'fleece', 'Midlayer · Fleece': 'fleece',
  'Down': 'down_insulation', 'Midlayer · Down': 'down_insulation', 'Insulation · Down': 'down_insulation',
  'Insulation · Synthetic': 'synthetic_insulation', 'Insulation · Waterproof': 'synthetic_insulation', 'Midlayer · Other': 'synthetic_insulation',
  'Base layer · Merino': 'baselayer_merino', 'Base layer · Blends': 'baselayer_merino', 'Base layer · Synthetic': 'baselayer_synthetic', 'Base layer · Mesh': 'baselayer_synthetic',
  'Accessories': 'accessories', 'Beanies': 'accessories', 'Gloves': 'accessories', 'Hiking Beanies': 'accessories', 'Hiking Gloves': 'accessories',
  'Crampons': 'crampons',
  'Ice axes': 'ice_axes',
  'Ropes': 'ropes',
  'Backpacks': 'backpacks', 'Hiking Backpacks': 'backpacks',
  'Helmets': 'helmets',
  'Harnesses': 'harnesses',
  'Hardware': 'hardware', 'Crevasse rescue': 'hardware', 'Climbing other': 'hardware',
  'Trekking poles': 'trekking_poles',
  'Eyewear': 'eyewear',
  'Head lamps': 'electronics', 'Location': 'electronics'
};

const GEAR_CATEGORIES = [
  'Accessories',
  'Backpacks',
  'Base layer · Blends',
  'Base layer · Merino',
  'Base layer · Mesh',
  'Base layer · Synthetic',
  'Beanies',
  'Boots',
  'Climbing other',
  'Crampons',
  'Crevasse rescue',
  'Down',
  'Eyewear',
  'Fleece',
  'Gloves',
  'Hard shell · Jackets',
  'Hard shell · Pants',
  'Hardware',
  'Harnesses',
  'Head lamps',
  'Helmets',
  'Hiking Backpacks',
  'Hiking Beanies',
  'Hiking Boots',
  'Hiking Gloves',
  'Hiking Shoes',
  'Ice axes',
  'Insulation · Down',
  'Insulation · Synthetic',
  'Insulation · Waterproof',
  'Location',
  'Midlayer · Down',
  'Midlayer · Fleece',
  'Midlayer · Other',
  'Ropes',
  'Soft shell · Jackets',
  'Soft shell · Pants',
  'Trail running',
  'Trekking poles'
].sort();

const GEAR_HIERARCHY = {
  "Footwear": [
    "Boots", "Hiking Boots", "Hiking Shoes", "Trail running"
  ],
  "Clothing": [
    "Hard shell · Jackets", "Hard shell · Pants",
    "Soft shell · Jackets", "Soft shell · Pants",
    "Fleece", "Down", "Midlayer · Fleece", "Midlayer · Down", "Midlayer · Other",
    "Insulation · Waterproof", "Insulation · Synthetic", "Insulation · Down",
    "Base layer · Merino", "Base layer · Blends", "Base layer · Synthetic", "Base layer · Mesh",
    "Gloves", "Beanies", "Hiking Gloves", "Hiking Beanies", "Accessories"
  ],
  "Equipment": [
    "Backpacks", "Hiking Backpacks",
    "Crampons", "Ice axes", "Helmets", "Harnesses", "Ropes", "Hardware", "Crevasse rescue", "Climbing other", "Eyewear",
    "Head lamps", "Trekking poles", "Location"
  ]
};

const PRICE_SOURCE_OPTIONS = [
  'Mammut',
  'Mammut EU',
  'Mammut DE',
  'Mammut AT',
  'Mammut CH',
  'Mammut FR',
  'Mammut IT',
  'Mammut ES',
  'Mammut PT',
  'Mammut NL',
  'Mammut BE',
  'Mammut LU',
  'Mammut UK',
  'Mammut IE',
  'Mammut PL',
  'Mammut CZ',
  'Mammut SK',
  'Mammut HU',
  'Mammut SI',
  'Mammut HR',
  'Mammut RO',
  'Mammut BG',
  'Mammut DK',
  'Mammut SE',
  'Mammut NO',
  'Mammut FI',
  'Mammut EE',
  'Mammut LV',
  'Mammut LT',
  'Mammut US',
  'Mammut CA',
  'Mammut AU',
  'Mammut NZ',
  'Mammut JP',
  'Mammut DACH',
  'Mammut EMEA',
  'Mammut APAC',
  'Mammut NA',
  'Salomon',
  'Bergzeit',
  'Bever',
  'VerticalExtreme',
  'Gigasport',
  'Gigasport EU',
  'Gigasport DACH',
  'Gigasport AT',
  'Gigasport DE',
  'Gigasport IT',
  'Gigasport ES',
  'Gigasport FR',
  'Gigasport CH',
  'Mountain Warehouse'
];

const ADMIN_GEAR_SEARCH_PAGE_SIZE = 300;

const MAMMUT_REGION_PATTERNS = [
  { option: 'Mammut DACH', regex: /(\bdach\b|de[\/_\- ]?at[\/_\- ]?ch)/i },
  { option: 'Mammut EMEA', regex: /\bemea\b/i },
  { option: 'Mammut APAC', regex: /\bapac\b/i },
  { option: 'Mammut NA', regex: /(\bna\b|north[\s\-]?america)/i },
  { option: 'Mammut EU', regex: /(\beu\b|europe|european|europa)/i },
  { option: 'Mammut DE', regex: /(\bde\b|\bdeutschland\b|\bgermany\b)/i },
  { option: 'Mammut AT', regex: /(\bat\b|\baustria\b|\bösterreich\b)/i },
  { option: 'Mammut CH', regex: /(\bch\b|\bswitzerland\b|\bschweiz\b)/i },
  { option: 'Mammut FR', regex: /(\bfr\b|\bfrance\b)/i },
  { option: 'Mammut IT', regex: /(\bit\b|\bitaly\b|\bitalia\b)/i },
  { option: 'Mammut ES', regex: /(\bes\b|\bspain\b|\bespaña\b|\bespana\b)/i },
  { option: 'Mammut PT', regex: /(\bpt\b|\bportugal\b)/i },
  { option: 'Mammut NL', regex: /(\bnl\b|\bnetherlands\b|\bholland\b)/i },
  { option: 'Mammut BE', regex: /(\bbe\b|\bbelgium\b|\bbelgië\b|\bbelgie\b)/i },
  { option: 'Mammut LU', regex: /(\blu\b|\bluxembourg\b)/i },
  { option: 'Mammut UK', regex: /(\buk\b|united[\s\-]?kingdom|\bgreat[\s\-]?britain\b|\bbritain\b)/i },
  { option: 'Mammut IE', regex: /(\bie\b|\bireland\b)/i },
  { option: 'Mammut PL', regex: /(\bpl\b|\bpoland\b|\bpolska\b)/i },
  { option: 'Mammut CZ', regex: /(\bcz\b|\bczech\b|\bcesko\b)/i },
  { option: 'Mammut SK', regex: /(\bsk\b|\bslovakia\b|\bslovensko\b)/i },
  { option: 'Mammut HU', regex: /(\bhu\b|\bhungary\b|\bmagyarország\b|\bmagyarorszag\b)/i },
  { option: 'Mammut SI', regex: /(\bsi\b|\bslovenia\b|\bslovenija\b)/i },
  { option: 'Mammut HR', regex: /(\bhr\b|\bcroatia\b|\bhrvatska\b)/i },
  { option: 'Mammut RO', regex: /(\bro\b|\bromania\b|\bromânia\b|\bromania\b)/i },
  { option: 'Mammut BG', regex: /(\bbg\b|\bbulgaria\b|\bбългария\b)/i },
  { option: 'Mammut DK', regex: /(\bdk\b|\bdenmark\b|\bdanmark\b)/i },
  { option: 'Mammut SE', regex: /(\bse\b|\bsweden\b|\bsverige\b)/i },
  { option: 'Mammut NO', regex: /(\bno\b|\bnorway\b|\bnorge\b)/i },
  { option: 'Mammut FI', regex: /(\bfi\b|\bfinland\b|\bsuomi\b)/i },
  { option: 'Mammut EE', regex: /(\bee\b|\bestonia\b|\beesti\b)/i },
  { option: 'Mammut LV', regex: /(\blv\b|\blatvia\b|\blatvija\b)/i },
  { option: 'Mammut LT', regex: /(\blt\b|\blithuania\b|\blietuva\b)/i },
  { option: 'Mammut US', regex: /(\bus\b|\busa\b|united[\s\-]?states|\bamerica\b)/i },
  { option: 'Mammut CA', regex: /(\bca\b|\bcanada\b)/i },
  { option: 'Mammut AU', regex: /(\bau\b|\baustralia\b)/i },
  { option: 'Mammut NZ', regex: /(\bnz\b|\bnew[\s\-]?zealand\b)/i },
  { option: 'Mammut JP', regex: /(\bjp\b|\bjapan\b|\b日本\b)/i }
];

const resolveMammutSellerVariant = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('mammut')) return '';

  const regionMatch = MAMMUT_REGION_PATTERNS.find(({ regex }) => regex.test(normalized));
  if (regionMatch?.option) return regionMatch.option;

  return 'Mammut';
};

const GIGASPORT_REGION_PATTERNS = [
  { option: 'Gigasport DACH', regex: /(\bdach\b|de[\/_\- ]?at[\/_\- ]?ch)/i },
  { option: 'Gigasport EU', regex: /(\beu\b|europe|european|europa)/i },
  { option: 'Gigasport AT', regex: /(\bat\b|\baustria\b|\bösterreich\b)/i },
  { option: 'Gigasport DE', regex: /(\bde\b|\bdeutschland\b|\bgermany\b)/i },
  { option: 'Gigasport IT', regex: /(\bit\b|\bitaly\b|\bitalia\b)/i },
  { option: 'Gigasport ES', regex: /(\bes\b|\bspain\b|\bespaña\b|\bespana\b)/i },
  { option: 'Gigasport FR', regex: /(\bfr\b|\bfrance\b)/i },
  { option: 'Gigasport CH', regex: /(\bch\b|\bswitzerland\b|\bschweiz\b)/i }
];

const resolveGigasportSellerVariant = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('gigasport')) return '';

  const regionMatch = GIGASPORT_REGION_PATTERNS.find(({ regex }) => regex.test(normalized));
  if (regionMatch?.option) return regionMatch.option;

  return 'Gigasport';
};

const normalizeAllowedPriceSource = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';

  const exactMatch = PRICE_SOURCE_OPTIONS.find((option) => option.toLowerCase() === normalized);
  if (exactMatch) return exactMatch;

  const mammutVariant = resolveMammutSellerVariant(normalized);
  if (mammutVariant) return mammutVariant;
  const gigasportVariant = resolveGigasportSellerVariant(normalized);
  if (gigasportVariant) return gigasportVariant;
  if (normalized.includes('salomon')) return 'Salomon';
  if (normalized.includes('bergzeit')) return 'Bergzeit';
  if (normalized.includes('bever')) return 'Bever';
  if (normalized.includes('verticalextreme') || normalized.includes('vertical extreme')) return 'VerticalExtreme';
  if (normalized.includes('gigasport')) return 'Gigasport';
  if (normalized.includes('mountain warehouse') || normalized.includes('mountainwarehouse')) return 'Mountain Warehouse';

  return '';
};

const isWomensProduct = (item = {}) => {
  const gender = String(item?.gender || '').toLowerCase();
  if (gender.includes('women') || gender.includes('female')) return true;

  const name = String(item?.name || item?.model || '').toLowerCase();
  return /women|women's|womens|female|lady|ladies/.test(name);
};

const formatAdminProductLabel = (item = {}) => {
  const brand = String(item?.brand || '').trim();
  const name = String(item?.name || item?.model || '').trim();
  const base = [brand, name].filter(Boolean).join(' - ');
  return `${base}${isWomensProduct(item) ? ' W' : ''}`.trim();
};

const getRawGearAdminStatus = (item = {}) => {
  if (item?.hidden === true) {
    return { label: 'Hidden', color: '#6c757d', tone: 'hidden' };
  }

  if (item?.mergedTo) {
    return { label: 'Merged', color: '#fd7e14', tone: 'merged' };
  }

  const isPublished = Boolean(item?.validated || item?.displayId);
  if (isPublished) {
    return { label: 'Validated / Published', color: 'var(--error-color)', tone: 'validated' };
  }

  if (item?.adminModified) {
    return { label: 'Modified (not published)', color: '#fd7e14', tone: 'modified' };
  }

  return null;
};

const isImpactOrMammutItem = (item = {}) => {
  const normalized = (value) => String(value || '').trim().toLowerCase();
  const isImpact = (value) => normalized(value).includes('impact');
  const isMammutSignal = (value) => {
    const v = normalized(value);
    if (!v) return false;
    return v.includes('mammut');
  };

  const topLevelImpactSignals = [item?.affiliateNetwork, item?.source, item?.sourceCollection];
  const topLevelMammutSignals = [
    item?.brand,
    item?.brandLower,
    item?.merchant,
    item?.offerSeller,
    item?.seller,
    item?.merchantLink,
    item?.affiliateLink,
    item?.url,
    item?.link
  ];

  const hasTopLevelImpact = topLevelImpactSignals.some((value) => isImpact(value));
  const hasTopLevelMammutSignal = topLevelMammutSignals.some((value) => isMammutSignal(value));

  if (hasTopLevelImpact && hasTopLevelMammutSignal) {
    return true;
  }

  if (!Array.isArray(item?.offers)) return false;

  return item.offers.some((offer) => {
    const offerImpactSignals = [offer?.affiliateNetwork, offer?.sourceCollection, offer?.source];
    const offerMammutSignals = [offer?.seller, offer?.merchant, offer?.offerSeller, offer?.url, offer?.merchantLink, offer?.affiliateLink];

    const hasOfferImpact = offerImpactSignals.some((value) => isImpact(value));
    const hasOfferMammutSignal = offerMammutSignals.some((value) => isMammutSignal(value));

    return hasOfferImpact && hasOfferMammutSignal;
  });
};

const getPreferredPriceSource = (item = {}) => {
  const sellerCandidates = [
    item?.merchant,
    item?.seller,
    item?.offerSeller,
    item?.brand,
    item?.merchantLink,
    item?.affiliateLink,
    item?.link,
    item?.url,
    ...(Array.isArray(item?.offers)
      ? item.offers.flatMap((offer) => [
        offer?.seller,
        offer?.merchant,
        offer?.offerSeller,
        offer?.brand,
        offer?.url,
        offer?.merchantLink,
        offer?.affiliateLink
      ])
      : [])
  ];

  for (const candidate of sellerCandidates) {
    const normalized = normalizeAllowedPriceSource(candidate);
    if (normalized) return normalized;
  }

  if (isImpactOrMammutItem(item)) {
    return 'Mammut';
  }

  return '';
};

const CLOTHING_CATEGORIES = new Set(GEAR_HIERARCHY.Clothing || []);
const FOOTWEAR_CATEGORIES = new Set(GEAR_HIERARCHY.Footwear || []);
const EQUIPMENT_CATEGORIES = new Set(GEAR_HIERARCHY.Equipment || []);

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
  }

  const seconds = value?.seconds ?? value?._seconds;
  if (typeof seconds === 'number') {
    const millis = seconds * 1000;
    const fromSeconds = new Date(millis);
    return Number.isNaN(fromSeconds.getTime()) ? null : fromSeconds;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const formatTimestamp = (value) => {
  const parsed = toDateValue(value);
  return parsed ? parsed.toLocaleString() : '—';
};

const formatDurationMs = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return '—';

  const totalMs = Math.round(numeric);
  const totalSeconds = Math.floor(totalMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const formatCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return '—';
  return Math.round(numeric).toLocaleString();
};

const normalizeCertifications = (value) => {
  const rawValues = Array.isArray(value)
    ? value
    : String(value || '').split(',');

  const unique = new Set();
  const normalized = [];

  rawValues.forEach((entry) => {
    const cleaned = String(entry || '').trim();
    if (!cleaned) return;
    const key = cleaned.toLowerCase();
    if (unique.has(key)) return;
    unique.add(key);
    normalized.push(cleaned);
  });

  return normalized;
};

const isFieldRequiredForCategory = (field, category) => {
  const fieldName = field?.name;

  if (fieldName === 'boot_category' || fieldName === 'crampon_compat') {
    return category === 'Boots';
  }

  if (fieldName === 'waterproofing') {
    return category === 'Boots' || category === 'Hiking Boots';
  }

  if (fieldName === 'material_type') {
    return category === 'Beanies'
      || category === 'Hiking Beanies'
      || category === 'Gloves'
      || category === 'Hiking Gloves';
  }

  if (fieldName === 'gender') {
    return CLOTHING_CATEGORIES.has(category) || FOOTWEAR_CATEGORIES.has(category);
  }

  if (fieldName === 'material_composition' && EQUIPMENT_CATEGORIES.has(category)) {
    return false;
  }

  return !!field?.required;
};

const getFieldGuidance = (fieldName, category) => {
  if (!fieldName || !category) return '';

  if ((fieldName === 'boot_category' || fieldName === 'crampon_compat') && category !== 'Boots') {
    return 'Optional for this footwear type. Fill if the product explicitly lists compatibility.';
  }

  if (fieldName === 'waterproofing' && (category === 'Hiking Shoes' || category === 'Trail running')) {
    return 'Optional. Use when a membrane is explicitly stated (e.g. GORE-TEX).';
  }

  if ((fieldName === 'uiaa_falls' || fieldName === 'elongation_dynamic' || fieldName === 'dry_treatment') && category === 'Ropes') {
    return 'Optional but recommended for technical rope comparison quality.';
  }

  if ((fieldName === 'material' || fieldName === 'points') && category === 'Crampons') {
    return 'Optional but recommended for technical filtering.';
  }

  if ((fieldName === 'technical_rating' || fieldName === 'shaft_shape' || fieldName === 'head_type') && category === 'Ice axes') {
    return 'Optional but recommended for route/discipline matching.';
  }

  if (fieldName === 'rating' && category === 'Carabiners') {
    return 'Optional. Add EN/UIAA rating text when available.';
  }

  if ((fieldName === 'fill_weight' || fieldName === 'fill_power') && category === 'Down') {
    return 'Recommended for insulation comparability.';
  }

  if (fieldName === 'material_type' && ['Beanies', 'Hiking Beanies', 'Gloves', 'Hiking Gloves'].includes(category)) {
    return 'Required for accessories subcategory consistency.';
  }

  return '';
};

const getSchemaForCategory = (category) => {
  const specificKey = CATEGORY_TO_SCHEMA_KEY[category];
  const specificSchema = specificKey ? CATEGORY_SPECIFIC_PROPERTIES[specificKey] : [];
  return [...UNIVERSAL_PROPERTIES, ...specificSchema];
};

const mergeUniqueById = (existingItems = [], incomingItems = []) => {
  const map = new Map();

  for (const item of (Array.isArray(existingItems) ? existingItems : [])) {
    if (item?.id) map.set(item.id, item);
  }

  for (const item of (Array.isArray(incomingItems) ? incomingItems : [])) {
    if (item?.id) map.set(item.id, item);
  }

  return Array.from(map.values());
};

function ValidateGearPopup({ item, token, onClose, onValidate, onMerge, onUnmerge }) {
  const SEARCH_REQUEST_TIMEOUT_MS = 20000;
  const [mode, setMode] = useState('create'); // 'create' | 'merge'
  const [category, setCategory] = useState(item.category || '');
  
  // Dynamic Form Data
  const [formData, setFormData] = useState({});
  
  // Merge state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoadedCount, setSearchLoadedCount] = useState(0);
  const [searchCursor, setSearchCursor] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allowUntrackedLink, setAllowUntrackedLink] = useState(false);
  const [mergeNameOverrides, setMergeNameOverrides] = useState({});
  const [offerSellerOverrides, setOfferSellerOverrides] = useState({});
  const [offerCurrencyOverrides, setOfferCurrencyOverrides] = useState({});
  const [showUnmergePanel, setShowUnmergePanel] = useState(false);
  const [unmergeItems, setUnmergeItems] = useState([]);
  const [unmergeLoading, setUnmergeLoading] = useState(false);
  const [unmergeBusyIndex, setUnmergeBusyIndex] = useState(null);
  const [unmergeError, setUnmergeError] = useState('');

  const priceSourceOptions = useMemo(() => PRICE_SOURCE_OPTIONS, []);

  const hasTrackedLink = useMemo(() => {
    return Boolean(item?.affiliateLink || item?.aw_deep_link || item?.awDeepLink);
  }, [item]);

  const hasAnyLink = useMemo(() => {
    return Boolean(item?.affiliateLink || item?.aw_deep_link || item?.awDeepLink || item?.merchantLink || item?.link || item?.url);
  }, [item]);

  const requiresManualLinkAcceptance = item?.source !== 'displayed' && !hasTrackedLink;

  // Initialize form with defaults based on item and universal schema
  useEffect(() => {
    const sourceCategory = item.category || category || '';
    const schema = getSchemaForCategory(sourceCategory);

    const initialData = schema.reduce((acc, field) => {
      if (field.type === 'checkbox') {
        acc[field.name] = Boolean(item?.[field.name]);
        return acc;
      }

      if (field.type === 'tag-multiselect') {
        acc[field.name] = normalizeCertifications(item?.[field.name]);
        return acc;
      }

      const value = item?.[field.name];
      acc[field.name] = value !== undefined && value !== null ? value : '';
      return acc;
    }, {});

    initialData.brand = item.brand ?? initialData.brand ?? '';
    initialData.name = item.name ?? item.model ?? initialData.name ?? '';
    initialData.price = item.price ?? initialData.price ?? '';
    initialData.currency = String(item.currency ?? initialData.currency ?? 'EUR').toUpperCase();
    initialData.weight = item.grams ?? item.weight ?? initialData.weight ?? '';
    const prefilledSeller = getPreferredPriceSource(item);
    initialData.offerSeller = prefilledSeller || '';
    initialData.gender = item.gender ?? initialData.gender ?? '';
    initialData.material_composition = item.material_composition ?? initialData.material_composition ?? '';
    initialData.description = item.description ?? initialData.description ?? '';
    if (initialData.breathability !== '' && !initialData.breathability_metric) {
      initialData.breathability_metric = 'g/m²/24h';
    }

    const nextOfferSellerOverrides = {};
    const nextOfferCurrencyOverrides = {};
    if (Array.isArray(item.offers)) {
      item.offers.forEach((offer, index) => {
        const offerKey = String(offer?.offerId || `index:${index}`);
        nextOfferSellerOverrides[offerKey] = normalizeAllowedPriceSource(offer?.seller);
        nextOfferCurrencyOverrides[offerKey] = String(offer?.currency || item?.currency || 'EUR').toUpperCase();
      });
    }
    setOfferSellerOverrides(nextOfferSellerOverrides);
    setOfferCurrencyOverrides(nextOfferCurrencyOverrides);
    
    // Guess gender
    const lowerName = (item.name || '').toLowerCase();
    if (!initialData.gender) {
      if (lowerName.includes("women")) initialData.gender = "Women's";
      else if (lowerName.includes("men")) initialData.gender = "Men's";
    }

    setFormData(initialData);
    setShowUnmergePanel(false);
    setUnmergeItems([]);
    setUnmergeBusyIndex(null);
    setUnmergeError('');
  }, [item, category]);

  const loadUnmergePreview = async () => {
    if (item.source !== 'displayed' || !item.id) return;

    setUnmergeLoading(true);
    setUnmergeError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/${item.id}/unmerge-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.success) {
        setUnmergeError(data.message || 'Failed to load merged items.');
        setUnmergeItems([]);
        return;
      }

      setUnmergeItems(Array.isArray(data.data?.items) ? data.data.items : []);
    } catch (error) {
      setUnmergeError('Failed to load merged items.');
      setUnmergeItems([]);
    } finally {
      setUnmergeLoading(false);
    }
  };

  const handleToggleUnmergePanel = async () => {
    const nextValue = !showUnmergePanel;
    setShowUnmergePanel(nextValue);
    if (nextValue) {
      await loadUnmergePreview();
    }
  };

  const handleUnmergeItem = async (offerIndex) => {
    const safeIndex = Number.parseInt(offerIndex, 10);
    if (!Number.isFinite(safeIndex) || safeIndex < 0) return;

    if (unmergeItems.length <= 1) {
      alert('At least one merged item must remain.');
      return;
    }

    if (!window.confirm('Remove this merged item from the product?')) return;

    setUnmergeBusyIndex(safeIndex);
    setUnmergeError('');
    try {
      const result = await onUnmerge(item.id, safeIndex);
      if (!result?.success) {
        setUnmergeError(result?.message || 'Un-merge failed.');
        return;
      }

      await loadUnmergePreview();
    } catch (error) {
      setUnmergeError('Un-merge failed.');
    } finally {
      setUnmergeBusyIndex(null);
    }
  };

  const getSchema = () => {
    return getSchemaForCategory(category);
  };

  const certificationSuggestions = useMemo(() => {
    const specificKey = CATEGORY_TO_SCHEMA_KEY[category];
    const specificOptions = specificKey ? (CERTIFICATIONS_BY_SCHEMA_KEY[specificKey] || []) : [];
    return Array.from(new Set([...COMMON_CERTIFICATIONS, ...specificOptions]));
  }, [category]);

  const isFormValid = () => {
    if (!category) return false;
    const schema = getSchema();

    for (const field of schema) {
      if (isFieldRequiredForCategory(field, category)) {
        const val = formData[field.name];
        if (val === undefined || val === '' || val === null) return false;
        if (field.type === 'checkbox') continue; // false is valid
      }
    }

    return true;
  };

  const handleInputChange = (field, value) => {
    if (field === 'offerSeller') {
      const normalizedSeller = normalizeAllowedPriceSource(value);
      setFormData(prev => ({ ...prev, [field]: normalizedSeller }));
      return;
    }

    if (field === 'currency') {
      const normalizedCurrency = String(value || '').trim().toUpperCase();
      setFormData(prev => ({ ...prev, [field]: normalizedCurrency }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = async (requestedPage = 1) => {
    const normalizedQuery = String(searchQuery || '').trim();
    if (normalizedQuery.length < 2) {
      setSearchResults([]);
      setSearchPage(1);
      setSearchHasMore(false);
      setSearchLoadedCount(0);
      setSearchCursor(null);
      return;
    }
    setSearching(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_REQUEST_TIMEOUT_MS);
    try {
      const safePage = Math.max(1, Number.parseInt(requestedPage, 10) || 1);
      const isFirstPage = safePage <= 1;
      const cursor = isFirstPage ? '' : String(searchCursor || '');
      if (!isFirstPage && !cursor) {
        setSearchHasMore(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/search-displayed?q=${encodeURIComponent(normalizedQuery)}&cursor=${encodeURIComponent(cursor)}&pageSize=${ADMIN_GEAR_SEARCH_PAGE_SIZE}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      const data = await response.json();
      if (data.success) {
        const results = Array.isArray(data.data) ? data.data : [];
        setSearchResults((prev) => isFirstPage ? results : mergeUniqueById(prev, results));
        const pageData = data.page || {};
        const nextCursor = pageData.nextCursor ? String(pageData.nextCursor) : null;
        const cursorStalled = !isFirstPage && nextCursor && nextCursor === cursor;
        const hasMore = Boolean(pageData.hasMore) && Boolean(nextCursor) && !cursorStalled;
        setSearchPage(safePage);
        setSearchHasMore(hasMore);
        setSearchLoadedCount((prev) => isFirstPage ? results.length : prev + results.length);
        setSearchCursor(hasMore ? nextCursor : null);
        setMergeNameOverrides((prev) => {
          const next = { ...prev };
          results.forEach((product) => {
            if (!product?.id) return;
            if (next[product.id] === undefined) {
              next[product.id] = String(product.name || '');
            }
          });
          return next;
        });
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        console.warn('Displayed search request timed out.');
      } else {
        console.error(e);
      }
    } finally {
      clearTimeout(timeoutId);
      setSearching(false);
    }
  };

  const handleSubmitCreate = async () => {
    if (!isFormValid()) return alert('Please fill all required fields');
    if (item.source !== 'displayed' && !formData.offerSeller) return alert('Please select a webshop source');
    if (requiresManualLinkAcceptance && !allowUntrackedLink) {
      return alert(hasAnyLink
        ? 'Please explicitly accept adding a normal (untracked) link.'
        : 'Please explicitly accept adding this price without any link.');
    }
    setLoading(true);
    const submitPayload = { ...formData, allowUntrackedLink };
    if (item.source === 'displayed') {
      if (submitPayload.offerSeller) {
        submitPayload.merchant = submitPayload.offerSeller;
      }

      if (Array.isArray(item.offers) && item.offers.length > 0) {
        const offersPayload = item.offers
          .map((offer, index) => {
            const offerKey = String(offer?.offerId || `index:${index}`);
            const selectedSeller = normalizeAllowedPriceSource(offerSellerOverrides[offerKey]);
            if (!selectedSeller) return null;
            const selectedCurrency = String(
              offerCurrencyOverrides[offerKey]
              || offer?.currency
              || formData.currency
              || item?.currency
              || 'EUR'
            ).toUpperCase();
            return {
              offerId: offer?.offerId || '',
              offerIndex: index,
              seller: selectedSeller,
              currency: selectedCurrency
            };
          })
          .filter(Boolean);

        if (offersPayload.length > 0) {
          submitPayload.offers = offersPayload;
        }
      }

      delete submitPayload.offerSeller;
    }

    await onValidate(item.id, category, submitPayload);
    setLoading(false);
  };

  const handleMerge = async (targetProduct) => {
    // For merging, we might want to allow editing the *target product* too, 
    // but for now let's stick to attaching the offer.
    if (!window.confirm(`Merge this item into "${targetProduct.name}"?`)) return;
    const mergeName = String(mergeNameOverrides[targetProduct.id] ?? targetProduct.name ?? '').trim();

    if (!formData.offerSeller) return alert('Please select a webshop source');
    if (requiresManualLinkAcceptance && !allowUntrackedLink) {
      return alert(hasAnyLink
        ? 'Please explicitly accept adding a normal (untracked) link.'
        : 'Please explicitly accept adding this price without any link.');
    }
    setLoading(true);
    await onMerge(
      targetProduct.id,
      item.id,
      {
        price: formData.price,
        currency: formData.currency,
        offerSeller: formData.offerSeller,
        ...(mergeName ? { name: mergeName } : {})
      },
      allowUntrackedLink
    );
    setLoading(false);
  };

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popupContent}>
        <h3>{item.source === 'displayed' ? 'Edit Displayed Gear' : 'Validate Gear Item'}</h3>
        
        <div className={styles.tabGroup}>
          <button 
            onClick={() => setMode('create')}
            className={`${styles.tabButton} ${mode === 'create' ? styles.tabButtonActive : ''}`}
          >
            {item.source === 'displayed' ? 'Edit Details' : 'Create New Product'}
          </button>
          
          {item.source !== 'displayed' && (
            <button 
              onClick={() => setMode('merge')}
              className={`${styles.tabButton} ${mode === 'merge' ? styles.tabButtonActive : ''}`}
            >
              Merge into Existing
            </button>
          )}
        </div>

        <div className={styles.itemSummary}>
          <strong>Input Item:</strong> {formatAdminProductLabel(item)} <br/>
          <small>Original Price: {item.price} {item.currency}</small>
          {item.source !== 'displayed' && (
            <>
              <br />
              <small>
                Link status: {hasTrackedLink ? 'Tracked affiliate link available' : hasAnyLink ? 'Only normal link available' : 'No link available'}
              </small>
            </>
          )}
        </div>

        {mode === 'create' && (
          <>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Category (Required to load fields)</label>
              <select 
                value={category} 
                onChange={e => setCategory(e.target.value)} 
                className={styles.formSelect}
              >
                <option value="">Select Category</option>
                {Object.entries(GEAR_HIERARCHY).map(([group, options]) => (
                  <optgroup key={group} label={group}>
                    {options.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                ))}
                <optgroup label="Other">
                  {GEAR_CATEGORIES
                    .filter(c => !Object.values(GEAR_HIERARCHY).flat().includes(c))
                    .map(c => <option key={c} value={c}>{c}</option>)
                  }
                </optgroup>
              </select>
            </div>

            {category && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                {requiresManualLinkAcceptance && (
                  <div className={styles.formGroup} style={{ gridColumn: 'span 2', marginBottom: 0 }}>
                    <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={allowUntrackedLink}
                        onChange={e => setAllowUntrackedLink(e.target.checked)}
                      />
                      {hasAnyLink
                        ? 'I explicitly accept adding a normal (untracked) webshop link for this price.'
                        : 'I explicitly accept adding this price without any webshop link.'}
                    </label>
                  </div>
                )}

                <div className={styles.formGroup} style={{ gridColumn: 'span 2', marginBottom: 0 }}>
                  <label className={styles.formLabel}>Price Source (Webshop) {item.source !== 'displayed' && <span style={{ color: 'red' }}>*</span>}</label>
                  <select
                    value={formData.offerSeller || ''}
                    onChange={e => handleInputChange('offerSeller', e.target.value)}
                    className={styles.formSelect}
                    style={{ borderColor: item.source !== 'displayed' && !formData.offerSeller ? 'orange' : undefined }}
                  >
                    <option value="">Select webshop...</option>
                    {priceSourceOptions.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>

                {item.source === 'displayed' && Array.isArray(item.offers) && item.offers.length > 0 && (
                  <div className={styles.formGroup} style={{ gridColumn: 'span 2', marginBottom: 0 }}>
                    <label className={styles.formLabel}>Webshop per Price Offer</label>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {item.offers.map((offer, index) => {
                        const offerKey = String(offer?.offerId || `index:${index}`);
                        const currentSeller = offerSellerOverrides[offerKey] || '';
                        const currentCurrency = String(
                          offerCurrencyOverrides[offerKey]
                          || offer?.currency
                          || formData.currency
                          || item?.currency
                          || 'EUR'
                        ).toUpperCase();
                        return (
                          <div key={offerKey} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.8fr', gap: 8, alignItems: 'center' }}>
                            <div style={{ fontSize: '0.9em', color: '#555' }}>
                              Offer {index + 1}: {offer?.price ?? '-'} {offer?.currency || item?.currency || 'EUR'}
                            </div>
                            <select
                              value={currentSeller}
                              onChange={(e) => {
                                const selected = normalizeAllowedPriceSource(e.target.value);
                                setOfferSellerOverrides((prev) => ({ ...prev, [offerKey]: selected }));
                              }}
                              className={styles.formSelect}
                            >
                              <option value="">Select webshop...</option>
                              {priceSourceOptions.map(source => (
                                <option key={source} value={source}>{source}</option>
                              ))}
                            </select>
                            <select
                              value={currentCurrency}
                              onChange={(e) => {
                                const selectedCurrency = String(e.target.value || 'EUR').toUpperCase();
                                setOfferCurrencyOverrides((prev) => ({ ...prev, [offerKey]: selectedCurrency }));
                              }}
                              className={styles.formSelect}
                            >
                              {CURRENCY_OPTIONS.map((currencyCode) => (
                                <option key={currencyCode} value={currencyCode}>{currencyCode}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {item.source === 'displayed' && (
                  <div className={styles.formGroup} style={{ gridColumn: 'span 2', marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <label className={styles.formLabel} style={{ marginBottom: 0 }}>Merged Items</label>
                      <button
                        type="button"
                        onClick={handleToggleUnmergePanel}
                        className={styles.cancelButton}
                        style={{ width: 'auto', padding: '6px 12px' }}
                        disabled={loading || unmergeLoading}
                      >
                        {showUnmergePanel ? 'Hide Un-merge' : 'Un-merge'}
                      </button>
                    </div>

                    {showUnmergePanel && (
                      <div style={{ marginTop: 8, border: '1px solid var(--border-color)', borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
                        {unmergeLoading && (
                          <div style={{ padding: 10, color: 'var(--text-muted)' }}>Loading merged items…</div>
                        )}

                        {!unmergeLoading && unmergeError && (
                          <div style={{ padding: 10, color: '#b00' }}>{unmergeError}</div>
                        )}

                        {!unmergeLoading && !unmergeError && unmergeItems.length === 0 && (
                          <div style={{ padding: 10, color: 'var(--text-muted)' }}>No merged items available.</div>
                        )}

                        {!unmergeLoading && !unmergeError && unmergeItems.map((mergeItem) => {
                          const isLast = unmergeItems.length <= 1;
                          const isBusy = unmergeBusyIndex === mergeItem.offerIndex;
                          return (
                            <div
                              key={`${mergeItem.offerId || 'offer'}-${mergeItem.offerIndex}`}
                              style={{
                                padding: 10,
                                borderBottom: '1px solid var(--border-color)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 12
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600 }}>{mergeItem.originalName || `Offer ${mergeItem.offerIndex + 1}`}</div>
                                <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                  Price: {mergeItem.price ?? '—'} {mergeItem.currency || 'EUR'}
                                </div>
                                <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                  Webshop: {mergeItem.webshop || '—'} · Source: {mergeItem.source || '—'}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUnmergeItem(mergeItem.offerIndex)}
                                className={styles.cancelButton}
                                style={{ width: 'auto', whiteSpace: 'nowrap' }}
                                disabled={loading || isBusy || isLast}
                                title={isLast ? 'Cannot remove the last merged item.' : 'Remove this merged item'}
                              >
                                {isBusy ? 'Removing…' : 'Remove from merge'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {getSchema().map(field => (
                  <div key={field.name} className={styles.formGroup} style={{ gridColumn: field.type === 'textarea' ? 'span 2' : 'auto', marginBottom: 0 }}>
                    {(() => {
                      const isFieldRequired = isFieldRequiredForCategory(field, category);
                      const fieldValue = formData[field.name];
                      const hasValue = Array.isArray(fieldValue)
                        ? fieldValue.length > 0
                        : fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
                      const fieldGuidance = getFieldGuidance(field.name, category);
                      return (
                        <>
                    <label className={styles.formLabel}>
                      {field.name === 'breathability'
                        ? `Breathability (${formData.breathability_metric || 'Value'})`
                        : field.label} {isFieldRequired && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.name] || ''}
                        onChange={e => handleInputChange(field.name, e.target.value)}
                        className={styles.formSelect}
                        style={{ borderColor: isFieldRequired && !hasValue ? 'orange' : undefined }}
                      >
                        <option value="">Select...</option>
                        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'textarea' ? (
                       <textarea
                        value={formData[field.name] || ''}
                        onChange={e => handleInputChange(field.name, e.target.value)}
                        className={styles.formTextarea}
                        style={{ minHeight: 80 }}
                       />
                    ) : field.type === 'checkbox' ? (
                       <input 
                         type="checkbox"
                         checked={!!formData[field.name]}
                         onChange={e => handleInputChange(field.name, e.target.checked)}
                         style={{ transform: 'scale(1.2)', marginLeft: 5 }}
                       />
                    ) : field.type === 'tag-multiselect' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {(field.options || certificationSuggestions).map((option) => {
                          const valueArray = Array.isArray(formData[field.name]) ? formData[field.name] : [];
                          const selected = valueArray.includes(option);
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => {
                                const current = valueArray;
                                const next = selected
                                  ? current.filter((item) => item !== option)
                                  : [...current, option];
                                handleInputChange(field.name, next);
                              }}
                              style={{
                                border: '1px solid',
                                borderColor: selected ? '#007bff' : '#d0d0d0',
                                background: selected ? '#eaf3ff' : '#fff',
                                color: selected ? '#0056b3' : '#333',
                                borderRadius: 999,
                                padding: '4px 10px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        value={formData[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={e => handleInputChange(field.name, e.target.value)}
                        className={styles.formInput}
                        style={{ borderColor: isFieldRequired && !hasValue ? 'orange' : undefined }}
                      />
                    )}
                    {field.name === 'certifications' && certificationSuggestions.length > 0 && (
                      <small className={styles.recommendedHint}>
                        Choose one or more tags for this category.
                      </small>
                    )}
                    {fieldGuidance && (
                      <small className={styles.recommendedHint}>
                        {fieldGuidance}
                      </small>
                    )}
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}

            <div className={styles.buttonGroup}>
              <button onClick={onClose} className={styles.cancelButton} disabled={loading}>Cancel</button>
              <button 
                onClick={handleSubmitCreate} 
                disabled={loading || !category || !isFormValid()} 
                className={styles.submitButton}
              >
                {loading ? (item.source === 'displayed' ? 'Updating...' : 'Validating...') : (item.source === 'displayed' ? 'Update Product' : 'Validate & Publish')}
              </button>
            </div>
          </>
        )}

        {mode === 'merge' && (
          <>
             <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
               <input 
                  type="text" 
                  placeholder="Search existing products..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className={styles.formInput}
               />
               <button onClick={handleSearch} disabled={searching} className={styles.submitButton} style={{ width: 'auto' }}>Find</button>
             </div>

             <div className={styles.formGroup} style={{ marginBottom: 10 }}>
               <label className={styles.formLabel}>Price Source (Webshop) <span style={{ color: 'red' }}>*</span></label>
               <select
                 value={formData.offerSeller || ''}
                 onChange={e => handleInputChange('offerSeller', e.target.value)}
                 className={styles.formSelect}
                 style={{ borderColor: !formData.offerSeller ? 'orange' : undefined }}
               >
                 <option value="">Select webshop...</option>
                 {priceSourceOptions.map(source => (
                   <option key={source} value={source}>{source}</option>
                 ))}
               </select>
             </div>

             <div className={styles.formGroup} style={{ marginBottom: 10 }}>
               <label className={styles.formLabel}>Offer Currency <span style={{ color: 'red' }}>*</span></label>
               <select
                 value={formData.currency || 'EUR'}
                 onChange={e => handleInputChange('currency', e.target.value)}
                 className={styles.formSelect}
               >
                 {CURRENCY_OPTIONS.map((currencyCode) => (
                   <option key={currencyCode} value={currencyCode}>{currencyCode}</option>
                 ))}
               </select>
             </div>

             {requiresManualLinkAcceptance && (
               <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                 <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                   <input
                     type="checkbox"
                     checked={allowUntrackedLink}
                     onChange={e => setAllowUntrackedLink(e.target.checked)}
                   />
                   {hasAnyLink
                     ? 'I explicitly accept adding a normal (untracked) webshop link for this price.'
                     : 'I explicitly accept adding this price without any webshop link.'}
                 </label>
               </div>
             )}
             
             <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-color)', marginBottom: 15, borderRadius: 6 }}>
               {searchResults.map(prod => (
                 <div key={prod.id} style={{ padding: 10, borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <div style={{ flex: 1, minWidth: 0 }}>
                     <strong>{formatAdminProductLabel(prod)}</strong>
                     <div style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{prod.category}</div>
                     <div className={styles.formGroup} style={{ marginTop: 8, marginBottom: 0 }}>
                       <label className={styles.formLabel}>Product Name</label>
                       <input
                         type="text"
                         value={mergeNameOverrides[prod.id] ?? prod.name ?? ''}
                         onChange={(e) => {
                           const nextValue = e.target.value;
                           setMergeNameOverrides((prev) => ({ ...prev, [prod.id]: nextValue }));
                         }}
                         className={styles.formInput}
                         maxLength={60}
                       />
                     </div>
                   </div>
                   <button onClick={() => handleMerge(prod)} disabled={loading} className={styles.submitButton} style={{ fontSize: '0.8em', padding: '5px 10px', marginLeft: 12 }}>Merge</button>
                 </div>
               ))}
               {searchResults.length === 0 && !searching && <div style={{ padding: 10, color: 'var(--text-muted)', textAlign: 'center' }}>No results</div>}
             </div>
             <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
               <button
                 onClick={() => handleSearch(1)}
                 disabled={searching || searchPage <= 1}
                 className={styles.cancelButton}
                 style={{ width: 'auto' }}
               >
                 Reset Search
               </button>
               <button
                 onClick={() => handleSearch(searchPage + 1)}
                 disabled={searching || !searchHasMore}
                 className={styles.submitButton}
                 style={{ width: 'auto' }}
               >
                 Load More 300
               </button>
               <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                 Page {searchPage} • Loaded {searchLoadedCount}
               </span>
             </div>
             <div className={styles.buttonGroup}>
               <button onClick={onClose} className={styles.cancelButton} disabled={loading}>Cancel</button>
             </div>
          </>
        )}

      </div>
    </div>
  );
}

function TranslateLegalButton({ type, text }) {
  const [targetLang, setTargetLang] = useState('');
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const handleTranslate = async () => {
    setLoading(true);
    setError('');
    setShowDisclaimer(false);
    try {
      const result = await translateText(text, targetLang);
      setTranslated(result);
      setShowDisclaimer(true);
    } catch (e) {
      setError(e.message || 'Translation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <label>
        Translate to:
        <input
          type="text"
          value={targetLang}
          onChange={e => setTargetLang(e.target.value)}
          placeholder="e.g. es, fr, de"
          style={{ marginLeft: 8, width: 60 }}
        />
      </label>
      <button onClick={handleTranslate} disabled={loading || !targetLang || !text} style={{ marginLeft: 8 }}>
        {loading ? 'Translating...' : 'Translate'}
      </button>
      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
      {translated && (
        <div style={{ marginTop: 16, background: '#f9f9f9', padding: 12, borderRadius: 6 }}>
          <strong>Translated {type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}:</strong>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{translated}</div>
          {showDisclaimer && (
            <div style={{ marginTop: 12, fontSize: '0.95em', color: '#555' }}>
              <em>This is a machine translation for convenience. The legally binding version is the original English document.</em>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const AdminPage = () => {
  const SEARCH_REQUEST_TIMEOUT_MS = 20000;
  const [mountains, setMountains] = useState([]);
  const [gear, setGear] = useState([]);
  const [editorApplicants, setEditorApplicants] = useState([]);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState({ users: null, mountains: null, displayedGear: null });
  const [legalContent, setLegalContent] = useState({ privacy: "", terms: "" });
  const [legalVersions, setLegalVersions] = useState({ privacy: "1.0.0", terms: "1.0.0" });
  const [materialChange, setMaterialChange] = useState({ privacy: false, terms: false });
  const [legalLoading, setLegalLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gearLoading, setGearLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gearError, setGearError] = useState(null);
  const [userError, setUserError] = useState(null);
  const [adminSection, setAdminSection] = useState('users'); // users, mountains, gear, new-gear, legal, reports, applicants
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);
  const [newGear, setNewGear] = useState([]);
  const [newGearLoading, setNewGearLoading] = useState(false);
  const [newGearError, setNewGearError] = useState(null);
  const [gearSearchQuery, setGearSearchQuery] = useState('');
  const [legacyGearSearchQuery, setLegacyGearSearchQuery] = useState('');
  const [displayedGear, setDisplayedGear] = useState([]);
  const [displayedGearLoading, setDisplayedGearLoading] = useState(false);
  const [displayedGearError, setDisplayedGearError] = useState(null);
  const [displayedGearSearchQuery, setDisplayedGearSearchQuery] = useState('');
  const [displayedGearTotalCount, setDisplayedGearTotalCount] = useState(0);
  const [displayedGearSearchPage, setDisplayedGearSearchPage] = useState(1);
  const [displayedGearSearchHasMore, setDisplayedGearSearchHasMore] = useState(false);
  const [displayedGearSearchLoadedCount, setDisplayedGearSearchLoadedCount] = useState(0);
  const [displayedGearSearchCursor, setDisplayedGearSearchCursor] = useState(null);
  
  const [rawGear, setRawGear] = useState([]);
  const [rawGearLoading, setRawGearLoading] = useState(false);
  const [hiddenGear, setHiddenGear] = useState([]);
  const [hiddenGearNextCursor, setHiddenGearNextCursor] = useState(null);
  const [hiddenGearCursorHistory, setHiddenGearCursorHistory] = useState([]);
  const [hiddenGearHasMore, setHiddenGearHasMore] = useState(false);
  const [hiddenGearPageIndex, setHiddenGearPageIndex] = useState(1);
  const [hiddenGearTotalCount, setHiddenGearTotalCount] = useState(0);
  const [rawGearNextCursor, setRawGearNextCursor] = useState(null);
  const [rawGearCursorHistory, setRawGearCursorHistory] = useState([]);
  const [rawGearHasMore, setRawGearHasMore] = useState(false);
  const [rawGearPageIndex, setRawGearPageIndex] = useState(1);
  const [rawGearTotalCount, setRawGearTotalCount] = useState(0);
  const [rawGearSearchPage, setRawGearSearchPage] = useState(1);
  const [rawGearSearchHasMore, setRawGearSearchHasMore] = useState(false);
  const [rawGearSearchLoadedCount, setRawGearSearchLoadedCount] = useState(0);
  const [rawGearSearchCursor, setRawGearSearchCursor] = useState(null);
  const [newGearSearchPage, setNewGearSearchPage] = useState(1);
  const [newGearSearchHasMore, setNewGearSearchHasMore] = useState(false);
  const [newGearSearchLoadedCount, setNewGearSearchLoadedCount] = useState(0);
  const [newGearSearchCursor, setNewGearSearchCursor] = useState(null);
  const [gearUpdateLoading, setGearUpdateLoading] = useState(false);
  const [gearUpdateMessage, setGearUpdateMessage] = useState('');
  const [gearUpdateStatus, setGearUpdateStatus] = useState(null);

  const [validatingItem, setValidatingItem] = useState(null);

  const { currentUser } = useUser();
  // Fetch reports from Firestore
  const fetchReports = async () => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      console.log('[AdminPage] Reports snapshot:', snapshot);
      const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('[AdminPage] Fetched reports:', reportsData);
      setReports(reportsData);
    } catch (e) {
      setReportsError('Failed to fetch reports.');
      console.error('[AdminPage] Error fetching reports:', e);
    } finally {
      setReportsLoading(false);
    }
  };
  useEffect(() => {
    if (!currentUser) return;
    if (adminSection === 'applicants') fetchEditorApplicants();
    if (adminSection === 'users') fetchUsersForAdmin();
    if (adminSection === 'mountains') fetchMountainsForAdmin();
    if (adminSection === 'gear') {
      setRawGear([]);
      setRawGearSearchPage(1);
      setRawGearSearchHasMore(false);
      setRawGearSearchLoadedCount(0);
      setRawGearSearchCursor(null);
      setRawGearTotalCount(0);
      fetchGearUpdateStatus();
    }
    if (adminSection === 'hidden-gear') fetchRawGear(true, { reset: true });
    if (adminSection === 'displayed-gear') {
      setDisplayedGear([]);
      setDisplayedGearSearchPage(1);
      setDisplayedGearSearchHasMore(false);
      setDisplayedGearSearchLoadedCount(0);
      setDisplayedGearSearchCursor(null);
      setDisplayedGearTotalCount(0);
    }
    if (adminSection === 'new-gear') fetchNewGear();
    if (adminSection === 'reports') fetchReports();
    if (adminSection === 'legal') {
      fetchLegal();
    }
  }, [adminSection, currentUser]);

  const fetchRawGear = async (hidden = false, options = {}) => {
    const { cursor = '', reset = false, history = null, pageIndex = 1 } = options;
    setRawGearLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('hidden', String(hidden));
      params.set('limit', '500');
      if (cursor) {
        params.set('cursor', cursor);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/raw-gear?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      const data = await response.json();
      if (data.success) {
        if (hidden) {
          const hiddenItems = Array.isArray(data.data) ? data.data : [];
          setHiddenGear(hiddenItems);
          setHiddenGearNextCursor(data.page?.nextCursor || null);
          setHiddenGearHasMore(Boolean(data.page?.hasMore));
          setHiddenGearTotalCount(hiddenItems.length);
          if (reset) {
            setHiddenGearCursorHistory([]);
            setHiddenGearPageIndex(1);
          } else {
            if (Array.isArray(history)) {
              setHiddenGearCursorHistory(history);
            }
            setHiddenGearPageIndex(pageIndex);
          }
          return;
        }

        const rawItems = Array.isArray(data.data) ? data.data : [];
        setRawGear(rawItems);
        setRawGearNextCursor(data.page?.nextCursor || null);
        setRawGearHasMore(Boolean(data.page?.hasMore));
        setRawGearTotalCount(rawItems.length);
        if (reset) {
          setRawGearCursorHistory([]);
          setRawGearPageIndex(1);
        } else {
          if (Array.isArray(history)) {
            setRawGearCursorHistory(history);
          }
          setRawGearPageIndex(pageIndex);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRawGearLoading(false);
    }
  };

  const handleRawGearSearch = async () => {
    return handleRawGearSearchPage(1);
  };

  const handleRawGearSearchPage = async (page = 1) => {
    const queryText = legacyGearSearchQuery.trim();
    if (!queryText) {
      setRawGear([]);
      setRawGearSearchPage(1);
      setRawGearSearchHasMore(false);
      setRawGearSearchLoadedCount(0);
      setRawGearSearchCursor(null);
      setRawGearTotalCount(0);
      return;
    }

    setRawGearLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_REQUEST_TIMEOUT_MS);
    try {
      const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
      const isFirstPage = safePage <= 1;
      const cursor = isFirstPage ? '' : String(rawGearSearchCursor || '');
      if (!isFirstPage && !cursor) {
        setRawGearSearchHasMore(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/search?q=${encodeURIComponent(queryText)}&cursor=${encodeURIComponent(cursor)}&pageSize=${ADMIN_GEAR_SEARCH_PAGE_SIZE}`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` },
        signal: controller.signal
      });
      const data = await response.json();
      if (data.success) {
        const searchItems = Array.isArray(data.data) ? data.data : [];
        setRawGear((prev) => isFirstPage ? searchItems : mergeUniqueById(prev, searchItems));
        const pageData = data.page || {};
        const nextCursor = pageData.nextCursor ? String(pageData.nextCursor) : null;
        const cursorStalled = !isFirstPage && nextCursor && nextCursor === cursor;
        const hasMore = Boolean(pageData.hasMore) && Boolean(nextCursor) && !cursorStalled;
        setRawGearHasMore(Boolean(pageData.hasMore));
        setRawGearNextCursor(null);
        setRawGearCursorHistory([]);
        setRawGearPageIndex(safePage);
        setRawGearSearchPage(safePage);
        setRawGearSearchHasMore(hasMore);
        let nextLoadedCount = 0;
        setRawGearSearchLoadedCount((prev) => {
          nextLoadedCount = isFirstPage ? searchItems.length : (prev + searchItems.length);
          return nextLoadedCount;
        });
        setRawGearSearchCursor(hasMore ? nextCursor : null);
        setRawGearTotalCount(nextLoadedCount);
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        console.warn('Raw gear search timed out. Please narrow your query.');
      } else {
        console.error(e);
      }
    } finally {
      clearTimeout(timeoutId);
      setRawGearLoading(false);
    }
  };

  const fetchGearUpdateStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/update/status`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      const data = await response.json();
      if (data.success) {
        setGearUpdateStatus(data.data);
        return data.data;
      }
    } catch (e) {
      console.error('Failed to fetch gear update status:', e);
    }
    return null;
  };

  const handleRunGearUpdate = async () => {
    setGearUpdateLoading(true);
    setGearUpdateMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setGearUpdateMessage(data.message || 'Gear update queued.');
        await fetchGearUpdateStatus();

        const pollInterval = setInterval(async () => {
          const status = await fetchGearUpdateStatus();
          if (status && ['completed', 'failed', 'skipped'].includes(status.status)) {
            clearInterval(pollInterval);
          }
        }, 5000);
        setTimeout(() => clearInterval(pollInterval), 10 * 60 * 1000);
      } else {
        setGearUpdateMessage(data.message || 'Gear update failed.');
      }
    } catch (e) {
      setGearUpdateMessage('Gear update failed.');
      console.error(e);
    } finally {
      setGearUpdateLoading(false);
    }
  };

  const handleResetGearUpdate = async () => {
    setGearUpdateLoading(true);
    setGearUpdateMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/update/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setGearUpdateMessage(data.message || 'Update tasks reset.');
        await fetchGearUpdateStatus();
      } else {
        setGearUpdateMessage(data.message || 'Reset failed.');
      }
    } catch (e) {
      setGearUpdateMessage('Reset failed.');
      console.error(e);
    } finally {
      setGearUpdateLoading(false);
    }
  };

  const handleToggleHide = async (id, shouldHide) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/${id}/hide`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hidden: shouldHide })
      });
      const data = await response.json();
      if (data.success) {
        if (shouldHide) {
           setRawGear(prev => prev.filter(i => i.id !== id));
           setHiddenGear(prev => [...prev, { ...rawGear.find(i => i.id === id), hidden: true }]);
        } else {
           setHiddenGear(prev => prev.filter(i => i.id !== id));
           // Potentially add back to rawGear, but requiring refresh is safer or just let it exist in backend
           fetchRawGear(false); 
        }
      }
    } catch (e) {
      alert('Error toggling hide');
    }
  };

  const handleDisplayedGearSearch = async () => {
    return handleDisplayedGearSearchPage(1);
  };

  const handleDisplayedGearSearchPage = async (page = 1) => {
    const queryText = displayedGearSearchQuery.trim();
    if (!queryText) {
      setDisplayedGear([]);
      setDisplayedGearSearchPage(1);
      setDisplayedGearSearchHasMore(false);
      setDisplayedGearSearchLoadedCount(0);
      setDisplayedGearSearchCursor(null);
      setDisplayedGearTotalCount(0);
      return;
    }

    setDisplayedGearLoading(true);
    setDisplayedGearError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_REQUEST_TIMEOUT_MS);
    try {
      const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
      const isFirstPage = safePage <= 1;
      const cursor = isFirstPage ? '' : String(displayedGearSearchCursor || '');
      if (!isFirstPage && !cursor) {
        setDisplayedGearSearchHasMore(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/gear/search-displayed?q=${encodeURIComponent(queryText)}&cursor=${encodeURIComponent(cursor)}&pageSize=${ADMIN_GEAR_SEARCH_PAGE_SIZE}`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` },
        signal: controller.signal
      });
      const data = await response.json();
      if (data.success) {
        const items = Array.isArray(data.data) ? data.data : [];
        setDisplayedGear((prev) => isFirstPage ? items : mergeUniqueById(prev, items));
        const pageData = data.page || {};
        const nextCursor = pageData.nextCursor ? String(pageData.nextCursor) : null;
        const cursorStalled = !isFirstPage && nextCursor && nextCursor === cursor;
        const hasMore = Boolean(pageData.hasMore) && Boolean(nextCursor) && !cursorStalled;
        setDisplayedGearSearchPage(safePage);
        setDisplayedGearSearchHasMore(hasMore);
        let nextLoadedCount = 0;
        setDisplayedGearSearchLoadedCount((prev) => {
          nextLoadedCount = isFirstPage ? items.length : (prev + items.length);
          return nextLoadedCount;
        });
        setDisplayedGearSearchCursor(hasMore ? nextCursor : null);
        setDisplayedGearTotalCount(nextLoadedCount);
      } else {
        setDisplayedGearError(data.message || 'Failed to search displayed gear.');
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        setDisplayedGearError('Search timed out. Please narrow your query.');
      } else {
        setDisplayedGearError('Search failed.');
      }
    } finally {
      clearTimeout(timeoutId);
      setDisplayedGearLoading(false);
    }
  };


  const fetchMountainsForAdmin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/mountains`);
      const data = await response.json();
      if (data.success) {
        setMountains(data.data);
      }
    } catch (e) {
      console.error("Error fetching mountains:", e);
    }
  };

  const fetchGearForAdmin = async () => {
    setGearLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/gear`);
      const data = await response.json();
      if (data.success) {
        setGear(data.data);
      }
    } catch (e) {
      setGearError("Failed to fetch gear.");
    } finally {
      setGearLoading(false);
    }
  };

  const fetchNewGear = async () => {
    setNewGearLoading(true);
    setNewGearError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/new-gear`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setNewGear(data.data);
      } else {
        setNewGearError(data.message || 'Failed to fetch new gear.');
      }
    } catch (e) {
      setNewGearError('Error fetching new gear.');
      console.error(e);
    } finally {
      setNewGearLoading(false);
    }
  };

  const handleSearchGear = async () => {
    return handleSearchGearPage(1);
  };

  const handleSearchGearPage = async (page = 1) => {
    if (!gearSearchQuery.trim()) {
      setNewGearSearchPage(1);
      setNewGearSearchHasMore(false);
      setNewGearSearchLoadedCount(0);
      setNewGearSearchCursor(null);
      return fetchNewGear();
    }
    
    setNewGearLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_REQUEST_TIMEOUT_MS);
    try {
      const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
      const isFirstPage = safePage <= 1;
      const cursor = isFirstPage ? '' : String(newGearSearchCursor || '');
      if (!isFirstPage && !cursor) {
        setNewGearSearchHasMore(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/search?q=${encodeURIComponent(gearSearchQuery)}&cursor=${encodeURIComponent(cursor)}&pageSize=${ADMIN_GEAR_SEARCH_PAGE_SIZE}`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
        signal: controller.signal,
      });
      const data = await response.json();
      if (data.success) {
        const items = Array.isArray(data.data) ? data.data : [];
        const pageData = data.page || {};
        const nextCursor = pageData.nextCursor ? String(pageData.nextCursor) : null;
        const cursorStalled = !isFirstPage && nextCursor && nextCursor === cursor;
        const hasMore = Boolean(pageData.hasMore) && Boolean(nextCursor) && !cursorStalled;
        setNewGear((prev) => isFirstPage ? items : mergeUniqueById(prev, items));
        setNewGearSearchPage(safePage);
        setNewGearSearchHasMore(hasMore);
        setNewGearSearchLoadedCount((prev) => isFirstPage ? items.length : prev + items.length);
        setNewGearSearchCursor(hasMore ? nextCursor : null);
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        setNewGearError('Search timed out. Please narrow your query.');
      } else {
        console.error(e);
        setNewGearError('Search failed');
      }
    } finally {
      clearTimeout(timeoutId);
      setNewGearLoading(false);
    }
  };

  const handleValidateGear = async (id, category, modifiedData) => {
    try {
      const source = validatingItem?.source; // 'legacy', 'displayed' or default 'allgear'
      
      let url = `${API_BASE_URL}/api/admin/gear/${id}/validate`;
      let method = 'POST';
      
      if (source === 'displayed') {
         url = `${API_BASE_URL}/api/admin/gear/${id}`;
         method = 'PUT';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category, source, ...modifiedData })
      });
      const data = await response.json();
      if (data.success) {
        // Update list
        if (source === 'legacy') {
           setGear(prev => prev.filter(item => item.id !== id));
          } else if (source === 'displayed') {
            await handleDisplayedGearSearchPage(1);
        } else {
           setNewGear(prev => prev.filter(item => item.id !== id));
        }
        setValidatingItem(null);
      } else {
        alert(data.message || 'Validation failed');
      }
    } catch (e) {
      alert('Error validating gear');
      console.error(e);
    }
  };

  const handleMergeGear = async (targetProductId, rawItemId, overrideData, allowUntrackedLink = false) => {
    try {
      const source = validatingItem?.source;
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/merge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetProductId, rawItemId, overrideData, source, allowUntrackedLink })
      });
      const data = await response.json();
      if (data.success) {
        // Remove from list
        if (source === 'legacy') {
           setGear(prev => prev.filter(item => item.id !== rawItemId));
        } else {
           setNewGear(prev => prev.filter(item => item.id !== rawItemId));
        }
        setValidatingItem(null);
      } else {
        alert(data.message || 'Merge failed');
      }
    } catch (e) {
      alert('Error merging gear');
      console.error(e);
    }
  };

  const handleUnmergeGear = async (displayedProductId, offerIndex) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/${displayedProductId}/unmerge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ offerIndex })
      });
      const data = await response.json();
      if (!data.success) {
        return { success: false, message: data.message || 'Un-merge failed.' };
      }

      const updatedItem = data.data?.displayedItem || null;
      if (updatedItem?.id) {
        setDisplayedGear((prev) => prev.map((entry) => (entry.id === updatedItem.id ? { ...entry, ...updatedItem } : entry)));
        setValidatingItem((prev) => (prev?.id === updatedItem.id ? { ...prev, ...updatedItem } : prev));
      } else {
        await handleDisplayedGearSearchPage(1);
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error('Error un-merging offer:', error);
      return { success: false, message: 'Un-merge failed.' };
    }
  };

  const fetchEditorApplicants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/editorial-applicants`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setEditorApplicants(data.data);
      }
    } catch (e) {
      console.error("Error fetching applicants:", e);
    }
  };

  const fetchAdminMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/metrics`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMetrics(data.data);
      }
    } catch (e) {
      console.error("Error fetching admin metrics:", e);
    }
  };

  const fetchUsersForAdmin = async () => {
    setUserLoading(true);
    setUserError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      } else {
        setUserError(data.message || "Failed to fetch users.");
      }
    } catch (e) {
      setUserError("Error fetching users.");
    } finally {
      setUserLoading(false);
    }
  };

  const fetchLegal = async () => {
    setLegalLoading(true);
    try {
      const pSnap = await getDoc(doc(db, "legal", "privacy"));
      const tSnap = await getDoc(doc(db, "legal", "terms"));
      setLegalContent({
        privacy: pSnap.exists() ? pSnap.data().content : "",
        terms: tSnap.exists() ? tSnap.data().content : ""
      });
      setLegalVersions({
        privacy: pSnap.exists() ? pSnap.data().version : "1.0.0",
        terms: tSnap.exists() ? tSnap.data().version : "1.0.0"
      });
    } catch (e) {
      console.error("Error fetching legal docs:", e);
    } finally {
      setLegalLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchMountainsForAdmin(),
        fetchAdminMetrics(),
        fetchGearForAdmin(),
        fetchEditorApplicants(),
        fetchUsersForAdmin()
      ]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (adminSection === 'legal') {
      fetchLegal();
    }
  }, [adminSection]);

  const handleDeleteMountain = async (mountainId) => {
    if (!window.confirm("Are you sure you want to delete this mountain?")) return;
    try {
      let response = await fetch(`${API_BASE_URL}/api/admin/mountains/${mountainId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });

      if (response.status === 404) {
        response = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${currentUser.token}`,
          },
        });
      }

      const result = await response.json();
      if (result.success) {
        setMountains(prev => prev.filter(m => m.id !== mountainId));
      } else {
        alert(result.message || "Failed to delete mountain.");
      }
    } catch (e) {
      alert("Failed to delete mountain.");
    }
  };

  const handleDeleteGear = async (gearId) => {
    if (!window.confirm("Are you sure you want to delete this gear item?")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gear/${gearId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const result = await response.json().catch(() => ({ success: false, message: 'Failed to parse server response.' }));
      if (result.success) {
        setGear(prev => prev.filter(g => g.id !== gearId));
        setDisplayedGear(prev => prev.filter(g => g.id !== gearId));
      } else {
        alert(result.message || 'Failed to delete gear.');
      }
    } catch (e) {
      alert("Failed to delete gear.");
    }
  };

  const handleDeletePicture = async (mountainId, imageUrl) => {
    if (!window.confirm("Are you sure you want to delete this picture?")) return;
    try {
      let response = await fetch(`${API_BASE_URL}/api/admin/mountains/${mountainId}/pictures/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify({ imageUrl }),
      });

      if (response.status === 404) {
        response = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/pictures/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentUser.token}`,
          },
          body: JSON.stringify({ imageUrl }),
        });
      }

      const result = await response.json();
      if (result.success) {
        setMountains(prev => prev.map(m => {
          if (m.id === mountainId) {
            return { ...m, images: m.images.filter(img => (typeof img === 'string' ? img : img.url) !== imageUrl) };
          }
          return m;
        }));
      } else {
        alert(result.message || "Failed to delete picture.");
      }
    } catch (e) {
      alert("Failed to delete picture.");
    }
  };

  const handleEditorialDecision = async (userId, decision) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/editorial-decision`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, decision }),
      });
      const data = await response.json();
      if (data.success) {
        setEditorApplicants(prev => prev.filter(u => u._id !== userId));
        fetchUsersForAdmin(); // Refresh users to show new roles
      }
    } catch (e) {
      alert("Failed to process decision.");
    }
  };

  const handleRevokeEditorRole = async (userId, username) => {
    if (!window.confirm(`Revoke editor role from @${username}?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/revoke-editor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u._id === userId ? { ...u, role: 'user' } : u));
      }
    } catch (e) {
      alert("Failed to revoke role.");
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.username === currentUser.username) {
      alert('You cannot delete your own admin account.');
      return;
    }
    if (!window.confirm(`Permanently delete user @${user.username}?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(prev => prev.filter(u => u._id !== user._id));
      }
    } catch (e) {
      alert("Failed to delete user.");
    }
  };

  const handleSaveLegal = async (type) => {
    setLegalLoading(true);
    try {
      const newVersion = legalVersions[type] || "1.0.0";
      const isMaterial = materialChange[type];
      const safeContent = sanitizeHtml(legalContent[type]);

      await setDoc(doc(db, "legal", type), {
        content: safeContent,
        version: newVersion,
        isMaterial: isMaterial,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setLegalContent(prev => ({ ...prev, [type]: safeContent }));

      alert(`${type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} updated to version ${newVersion}${isMaterial ? ' (Material Change)' : ''}`);
    } catch (e) {
      console.error('[handleSaveLegal] Error:', e);
      setError("Failed to save legal document: " + e.message);
    } finally {
      setLegalLoading(false);
    }
  };

  const helmet = (
    <Helmet>
      <title>Admin Panel | PeaksOnFoot</title>
    </Helmet>
  );

  const renderGearThumb = (item) => {
    const src = item?.image || item?.imageUrl;
    if (!src) {
      return <div className={styles.gearThumbPlaceholder}>No image</div>;
    }

    return <img src={src} alt={item?.name || 'Gear'} className={styles.gearThumbImage} />;
  };

  const matchesGearQuery = (item, queryText) => {
    const q = String(queryText || '').trim().toLowerCase();
    if (!q) return true;
    const displayLabel = formatAdminProductLabel(item).toLowerCase();
    return Boolean(
      item.name?.toLowerCase().includes(q)
      || item.brand?.toLowerCase().includes(q)
      || item.category?.toLowerCase().includes(q)
      || displayLabel.includes(q)
    );
  };

  const isRawSearchActive = Boolean(legacyGearSearchQuery.trim());
  const rawGearFiltered = isRawSearchActive ? rawGear : [];
  const rawGearPrioritized = useMemo(() => {
    const rankItem = (item = {}) => {
      const isValidated = Boolean(item?.validated || item?.displayId);
      const isMerged = Boolean(item?.mergedTo);
      if (isValidated) return 0;
      if (isMerged) return 1;
      return 2;
    };

    return rawGearFiltered
      .map((item, index) => ({ item, index, rank: rankItem(item) }))
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        return a.index - b.index;
      })
      .map(({ item }) => item);
  }, [rawGearFiltered]);

  if (loading) return <div className={styles.adminPage}><p>Loading admin data...</p></div>;

  return (
    <>
      {helmet}
      <div className={styles.adminPage}>
        <h1>Admin Panel</h1>
        {validatingItem && (
          <ValidateGearPopup 
            item={validatingItem} 
            token={currentUser?.token}
            onClose={() => setValidatingItem(null)} 
            onValidate={handleValidateGear} 
            onMerge={handleMergeGear}
            onUnmerge={handleUnmergeGear}
          />
        )}
        
        <div className={styles.adminNav} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '20px 0' }}>
          <button onClick={() => setAdminSection('users')} className={adminSection === 'users' ? styles.activeNav : ''}>Users ({metrics.users ?? users.length})</button>
          <button onClick={() => setAdminSection('mountains')} className={adminSection === 'mountains' ? styles.activeNav : ''}>Mountains ({metrics.mountains ?? mountains.length})</button>
          <button onClick={() => setAdminSection('gear')} className={adminSection === 'gear' ? styles.activeNav : ''}>Gear</button>
          <button onClick={() => setAdminSection('displayed-gear')} className={adminSection === 'displayed-gear' ? styles.activeNav : ''}>Displayed Gear ({metrics.displayedGear ?? displayedGearTotalCount ?? displayedGear.length})</button>
          <button onClick={() => setAdminSection('hidden-gear')} className={adminSection === 'hidden-gear' ? styles.activeNav : ''}>Hidden Gear</button>
          <button onClick={() => setAdminSection('new-gear')} className={adminSection === 'new-gear' ? styles.activeNav : ''}>New Gear</button>
          <button onClick={() => setAdminSection('legal')} className={adminSection === 'legal' ? styles.activeNav : ''}>Legal</button>
          <button onClick={() => setAdminSection('reports')} className={adminSection === 'reports' ? styles.activeNav : ''}>Reports</button>
          <button onClick={() => setAdminSection('applicants')} className={adminSection === 'applicants' ? styles.activeNav : ''}>Applicants</button>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {adminSection === 'applicants' && (
          <section className={styles.section}>
            <h2>Editorial Applicants</h2>
            <button onClick={fetchEditorApplicants}>Refresh</button>
            {editorApplicants.length > 0 ? (
              <ul className={styles.list}>
                {editorApplicants.map(user => (
                  <li key={user._id} className={styles.listItem}>
                    <div className={styles.mountainDetails}>
                      <span>{user.nickname || user.username} (@{user.username})</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className={styles.deleteButton} onClick={() => handleEditorialDecision(user._id, 'accept')}>Accept</button>
                        <button className={styles.deleteButton} onClick={() => handleEditorialDecision(user._id, 'decline')}>Decline</button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p>No pending applications.</p>}
          </section>
        )}

        {adminSection === 'users' && (
          <section className={styles.section}>
            <h2>Manage Users</h2>
            <button onClick={fetchUsersForAdmin} disabled={userLoading}>
              {userLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            {userError && <p className={styles.error}>{userError}</p>}
            {users.length > 0 ? (
              <ul className={styles.list}>
                {users.map(user => (
                  <li key={user._id} className={styles.listItem}>
                    <div className={styles.mountainDetails}>
                      <span>
                        {user.nickname || user.username} (@{user.username}) — <strong>{user.role || 'user'}</strong>
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {user.role === 'editor' && (
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleRevokeEditorRole(user._id, user.username)}
                            disabled={user._id === currentUser?._id}
                          >
                            Revoke Editor
                          </button>
                        )}
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteUser(user)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p>No users found.</p>}
          </section>
        )}

        {adminSection === 'mountains' && (
          <section className={styles.section}>
            <h2>Manage Mountains and Pictures</h2>
            <button onClick={fetchMountainsForAdmin}>Refresh</button>
            {mountains.length > 0 ? (
              <ul className={styles.list}>
                {mountains.map(mountain => (
                  <li key={mountain.id} className={styles.listItem}>
                    <div className={styles.mountainDetails}>
                      <span>{mountain.name}</span>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeleteMountain(mountain.id)}
                      >
                        Delete Mountain
                      </button>
                    </div>
                    {mountain.images && mountain.images.length > 0 && (
                      <div className={styles.imageGalleryAdmin}>
                        <h3>Pictures:</h3>
                        <div className={styles.imageGrid}>
                          {mountain.images.map((img, idx) => {
                            const url = typeof img === 'string' ? img : img.url;
                            return (
                              <div key={`${mountain.id}_${idx}`} className={styles.imageItem}>
                                <img src={url} alt="Mountain" className={styles.adminImagePreview} />
                                <button className={styles.deletePictureButton} onClick={() => handleDeletePicture(mountain.id, url)}>Delete</button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p>No mountains found.</p>}
          </section>
        )}

        {adminSection === 'reports' && (
          <section className={styles.section}>
            <h2>Reports</h2>
            {reportsLoading ? (
              <p>Loading reports…</p>
            ) : reportsError ? (
              <p className={styles.error}>{reportsError}</p>
            ) : reports.length > 0 ? (
              <ul className={styles.list}>
                {reports.map(report => {
                  // Prepare user lookup for context messages
                  let userMap = {};
                  if (Array.isArray(report.users)) {
                    for (const u of report.users) {
                      if (u && (u.authUid || u.uid)) {
                        userMap[u.authUid || u.uid] = u.username || u.nickname || u.displayName || (u.authUid || u.uid);
                      }
                    }
                  }
                  // Helper to get username by senderId
                  const getUsername = (senderId) =>
                    (userMap[senderId]) ||
                    (Array.isArray(report.users) && report.users.find(u => (u.authUid || u.uid) === senderId)?.username) ||
                    senderId?.slice?.(0, 8) || "Unknown";

                  return (
                    <li key={report.id} className={styles.listItem}>
                      <div
                        style={{
                          background: "#f9f9f9",
                          borderRadius: 8,
                          padding: 16,
                          margin: "8px 0",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                          maxWidth: 520,
                          wordBreak: "break-word"
                        }}
                      >
                        {/* Usernames */}
                        <div style={{ marginBottom: 10 }}>
                          <strong>Users:</strong>{" "}
                          {Array.isArray(report.users)
                            ? report.users.map(u =>
                                u.username || u.nickname || u.displayName || (u.authUid || u.uid || "Unknown")
                              ).join(", ")
                            : (report.username || report.user || "N/A")}
                        </div>
                        {/* Context messages as chat transcript */}
                        {report.contextMessages && Array.isArray(report.contextMessages) && (
                          <div style={{ marginBottom: 10 }}>
                            <strong>Context:</strong>
                            <div style={{
                              background: "#fff",
                              border: "1px solid #eee",
                              borderRadius: 6,
                              padding: "8px 10px",
                              marginTop: 4,
                              fontSize: "0.98em",
                              maxHeight: 220,
                              overflowY: "auto"
                            }}>
                              {report.contextMessages.map((msg, idx) => {
                                if (!msg || typeof msg !== "object") return null;
                                const username = getUsername(msg.senderId);
                                const text = msg.text || "";
                                const deleted = msg.deleted;
                                let ts = "";
                                if (msg.createdAt && typeof msg.createdAt === "object") {
                                  if (msg.createdAt.seconds) {
                                    ts = new Date(msg.createdAt.seconds * 1000).toLocaleString();
                                  }
                                }
                                return (
                                  <div key={msg.id || idx} style={{ opacity: deleted ? 0.5 : 1, marginBottom: 2 }}>
                                    <span style={{ color: "#1976d2", fontWeight: 500 }}>{username}</span>
                                    <span style={{ color: "#888", fontSize: "0.93em", marginLeft: 6 }}>{ts}</span>
                                    <span style={{ marginLeft: 10, color: deleted ? "#b00" : "#222" }}>
                                      {deleted ? <em>(deleted)</em> : text}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Report message */}
                        <div style={{ marginTop: 8 }}>
                          <strong>Report details:</strong>{" "}
                          <span style={{ color: "#b00" }}>
                            {report.message || report.reportMessage || report.reason || "(No message)"}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>No reports found.</p>
            )}
          </section>
        )}
        {adminSection === 'displayed-gear' && (
          <section className={styles.section}>
            <h2>Displayed Gear</h2>
            <p style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '0.9em' }}>
              {displayedGearSearchQuery.trim()
                ? `Search loaded: ${displayedGearSearchLoadedCount || displayedGear.length} | On this page: ${displayedGear.length}`
                : 'Type at least 2 characters and run search to load displayed gear.'}
            </p>
            <div style={{ display: 'flex', gap: 10, margin: '15px 0' }}>
               <input 
                  type="text" 
                  placeholder="Search displayed gear..." 
                  value={displayedGearSearchQuery}
                  onChange={e => setDisplayedGearSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleDisplayedGearSearch()}
                  style={{ padding: 8, flex: 1, border: '1px solid #ccc', borderRadius: 4 }}
               />
               <button onClick={handleDisplayedGearSearch} disabled={displayedGearLoading}>
                 {displayedGearLoading ? 'Searching…' : 'Search'}
               </button>
            </div>
            {displayedGearSearchQuery.trim() && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <button
                  onClick={() => handleDisplayedGearSearchPage(1)}
                  disabled={displayedGearLoading || displayedGearSearchPage <= 1}
                >
                  Reset Search
                </button>
                <button
                  onClick={() => handleDisplayedGearSearchPage(displayedGearSearchPage + 1)}
                  disabled={displayedGearLoading || !displayedGearSearchHasMore || !displayedGearSearchCursor}
                >
                  Load More 300
                </button>
                <span style={{ fontSize: '0.9em', color: '#666' }}>Page {displayedGearSearchPage}</span>
              </div>
            )}
            
            {displayedGearError && <p className={styles.error}>{displayedGearError}</p>}
            {displayedGear.length > 0 ? (
              <ul className={styles.list}>
                {displayedGear.map(item => (
                  <li key={item.id} className={`${styles.listItem} ${styles.gearListItem}`}>
                    <div className={styles.gearRow}>
                      <div className={styles.gearThumbWrap}>
                        {renderGearThumb(item)}
                      </div>
                      <div className={styles.gearRowMain}>
                        <div className={styles.mountainDetails}>
                          <span 
                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                            onClick={() => setValidatingItem({ ...item, source: 'displayed' })}
                          >
                            {formatAdminProductLabel(item)} ({item.category})
                          </span>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button 
                              style={{ background: '#007bff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => setValidatingItem({ ...item, source: 'displayed' })}
                            >
                              Edit
                            </button>
                            <button className={styles.deleteButton} onClick={() => handleDeleteGear(item.id)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p>No displayed gear found.</p>}
          </section>
        )}

        {adminSection === 'gear' && (
          <section className={styles.section}>
            <h2>Gear (Raw Feed)</h2>
            <p style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '0.9em' }}>
              {isRawSearchActive
                ? `Search loaded: ${rawGearSearchLoadedCount || rawGear.length} | On this page: ${rawGear.length}`
                : `All items: ${rawGearTotalCount} | On this page: ${rawGear.length} | Matching current filter: ${rawGearFiltered.length}`}
            </p>
            <div style={{ display: 'flex', gap: 10, margin: '15px 0' }}>
               <input 
                  type="text" 
                  placeholder="Search raw gear..." 
                  value={legacyGearSearchQuery}
                  onChange={e => setLegacyGearSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRawGearSearch()}
                  style={{ padding: 8, flex: 1, border: '1px solid #ccc', borderRadius: 4 }}
               />
               <button onClick={handleRawGearSearch} disabled={rawGearLoading}>
                 {rawGearLoading ? 'Searching…' : 'Search'}
               </button>
               <button onClick={() => fetchRawGear(false, { reset: true })} disabled={rawGearLoading}>
                 {rawGearLoading ? 'Refreshing…' : 'Refresh'}
               </button>
               <button onClick={handleRunGearUpdate} disabled={gearUpdateLoading || ['queued', 'running'].includes(gearUpdateStatus?.status)}>
                 {gearUpdateLoading || ['queued', 'running'].includes(gearUpdateStatus?.status) ? 'Updating…' : 'Run Gear Update'}
               </button>
               <button onClick={handleResetGearUpdate} disabled={gearUpdateLoading}>
                 Reset Update Status
               </button>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <button
                onClick={() => {
                  if (isRawSearchActive) {
                    handleRawGearSearchPage(1);
                    return;
                  }

                  if (rawGearCursorHistory.length === 0) return;
                  const nextHistory = rawGearCursorHistory.slice(0, -1);
                  const prevCursor = nextHistory.length ? nextHistory[nextHistory.length - 1] : '';
                  const prevPage = Math.max(1, rawGearPageIndex - 1);
                  fetchRawGear(false, { cursor: prevCursor, reset: false, history: nextHistory, pageIndex: prevPage });
                }}
                disabled={rawGearLoading || (isRawSearchActive ? (rawGearSearchPage || 1) <= 1 : rawGearCursorHistory.length === 0)}
              >
                {isRawSearchActive ? 'Reset Search' : 'Previous 500'}
              </button>
              <button
                onClick={() => {
                  if (isRawSearchActive) {
                    if (!rawGearSearchHasMore || !rawGearSearchCursor) return;
                    handleRawGearSearchPage((rawGearSearchPage || 1) + 1);
                    return;
                  }

                  if (!rawGearHasMore || !rawGearNextCursor) return;
                  const nextHistory = [...rawGearCursorHistory, rawGearNextCursor];
                  const nextPage = rawGearPageIndex + 1;
                  fetchRawGear(false, { cursor: rawGearNextCursor, reset: false, history: nextHistory, pageIndex: nextPage });
                }}
                disabled={rawGearLoading || (isRawSearchActive ? (!rawGearSearchHasMore || !rawGearSearchCursor) : (!rawGearHasMore || !rawGearNextCursor))}
              >
                {isRawSearchActive ? 'Load More 300' : 'Next 500'}
              </button>
              <span style={{ fontSize: '0.9em', color: '#666' }}>Page {isRawSearchActive ? rawGearSearchPage : rawGearPageIndex}</span>
            </div>
            {gearUpdateMessage && <p style={{ marginTop: 0 }}>{gearUpdateMessage}</p>}
            {gearUpdateStatus && (
              <div style={{ marginTop: 6, fontSize: '0.9em', color: '#555' }}>
                {(() => {
                  const runtime = gearUpdateStatus.runtime || {};
                  const result = gearUpdateStatus.result || {};
                  const stageDurations = runtime.stageDurationsMs || gearUpdateStatus.result?.stageDurationsMs || {};
                  const totalDurationMs = runtime.totalDurationMs ?? gearUpdateStatus.result?.totalDurationMs;
                  const rowsProcessed = result.rowsProcessed ?? result.processedCount;
                  const uniqueProductsFound = result.uniqueProductsFound;
                  const actualFirestoreWrites = result.actualFirestoreWrites ?? result.updatedCount;
                  const dedupedRows = Number.isFinite(Number(rowsProcessed)) && Number.isFinite(Number(uniqueProductsFound))
                    ? Math.max(0, Number(rowsProcessed) - Number(uniqueProductsFound))
                    : null;
                  const skippedWritesByHash = Number.isFinite(Number(uniqueProductsFound)) && Number.isFinite(Number(actualFirestoreWrites))
                    ? Math.max(0, Number(uniqueProductsFound) - Number(actualFirestoreWrites))
                    : null;

                  return (
                    <>
                <div><strong>Status:</strong> {gearUpdateStatus.status || 'unknown'}</div>
                {gearUpdateStatus.requestedAt && (
                  <div><strong>Requested:</strong> {formatTimestamp(gearUpdateStatus.requestedAt)}</div>
                )}
                {gearUpdateStatus.startedAt && (
                  <div><strong>Started:</strong> {formatTimestamp(gearUpdateStatus.startedAt)}</div>
                )}
                {gearUpdateStatus.finishedAt && (
                  <div><strong>Finished:</strong> {formatTimestamp(gearUpdateStatus.finishedAt)}</div>
                )}
                {gearUpdateStatus.lastHeartbeatAt && (
                  <div><strong>Last heartbeat:</strong> {formatTimestamp(gearUpdateStatus.lastHeartbeatAt)}</div>
                )}
                {(runtime.currentStage || gearUpdateStatus.stage) && (
                  <div><strong>Stage:</strong> {runtime.currentStage || gearUpdateStatus.stage}</div>
                )}
                <div><strong>Total runtime:</strong> {formatDurationMs(totalDurationMs)}</div>
                <div><strong>Feed update:</strong> {formatDurationMs(stageDurations.updateFeeds)}</div>
                <div><strong>Propagate displayed gear:</strong> {formatDurationMs(stageDurations.propagateDisplayedGear)}</div>
                <div><strong>Finalize:</strong> {formatDurationMs(stageDurations.finalize)}</div>
                <div><strong>Rows processed:</strong> {formatCount(rowsProcessed)}</div>
                <div><strong>Unique products found:</strong> {formatCount(uniqueProductsFound)}</div>
                <div><strong>Actual Firestore writes:</strong> {formatCount(actualFirestoreWrites)}</div>
                {dedupedRows !== null && (
                  <div><strong>Deduped rows in memory:</strong> {formatCount(dedupedRows)}</div>
                )}
                {skippedWritesByHash !== null && (
                  <div><strong>Writes skipped by hash-check:</strong> {formatCount(skippedWritesByHash)}</div>
                )}
                {gearUpdateStatus.result?.reason && (
                  <div><strong>Reason:</strong> {gearUpdateStatus.result.reason}</div>
                )}
                {gearUpdateStatus.error && (
                  <div style={{ color: '#b00' }}><strong>Error:</strong> {gearUpdateStatus.error}</div>
                )}
                    </>
                  );
                })()}
              </div>
            )}
            
            {rawGearLoading ? <p>Loading...</p> : rawGearPrioritized.length > 0 ? (
              <ul className={styles.list}>
                {rawGearPrioritized.map(item => {
                  const status = getRawGearAdminStatus(item);
                  const statusToneClass = status?.tone === 'validated'
                    ? styles.rawGearValidated
                    : status?.tone === 'merged'
                      ? styles.rawGearMerged
                      : '';

                  return (
                    <li
                      key={item.id}
                      className={`${styles.listItem} ${styles.gearListItem} ${isImpactOrMammutItem(item) ? styles.impactMammutFrame : ''}`}
                    >
                      <div className={styles.gearRow}>
                        <div className={styles.gearThumbWrap}>
                          {renderGearThumb(item)}
                        </div>
                        <div className={styles.gearRowMain}>
                          <div className={styles.mountainDetails} style={{ flex: 1 }}>
                            <span
                              className={statusToneClass}
                              style={{ cursor: 'pointer', fontWeight: 'bold' }}
                              onClick={() => setValidatingItem({ ...item, source: 'allgear' })}
                            >
                              {formatAdminProductLabel(item)}
                            </span>
                            <div className={statusToneClass} style={{ fontSize: '0.85em', marginTop: 4 }}>
                              Category: {item.category || 'Uncategorized'} | {item.price} {item.currency}
                            </div>
                            {status && (
                              <div style={{ fontSize: '0.8em', color: status.color }}>
                                • {status.label}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button
                              style={{ background: '#007bff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => setValidatingItem({ ...item, source: 'allgear' })}
                            >
                              Validate
                            </button>
                            <button
                              className={styles.deleteButton}
                              style={{ background: '#6c757d' }}
                              onClick={() => handleToggleHide(item.id, true)}
                            >
                              Hide
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : <p>No gear found.</p>}
          </section>
        )}

        {adminSection === 'hidden-gear' && (
          <section className={styles.section}>
            <h2>Hidden Gear</h2>
            <p style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '0.9em' }}>
              All items: {hiddenGearTotalCount} | On this page: {hiddenGear.length}
            </p>
            <button onClick={() => fetchRawGear(true, { reset: true })} disabled={rawGearLoading}>Refresh</button>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, marginTop: 10 }}>
              <button
                onClick={() => {
                  if (hiddenGearCursorHistory.length === 0) return;
                  const nextHistory = hiddenGearCursorHistory.slice(0, -1);
                  const prevCursor = nextHistory.length ? nextHistory[nextHistory.length - 1] : '';
                  const prevPage = Math.max(1, hiddenGearPageIndex - 1);
                  fetchRawGear(true, { cursor: prevCursor, reset: false, history: nextHistory, pageIndex: prevPage });
                }}
                disabled={rawGearLoading || hiddenGearCursorHistory.length === 0}
              >
                Previous 500
              </button>
              <button
                onClick={() => {
                  if (!hiddenGearHasMore || !hiddenGearNextCursor) return;
                  const nextHistory = [...hiddenGearCursorHistory, hiddenGearNextCursor];
                  const nextPage = hiddenGearPageIndex + 1;
                  fetchRawGear(true, { cursor: hiddenGearNextCursor, reset: false, history: nextHistory, pageIndex: nextPage });
                }}
                disabled={rawGearLoading || !hiddenGearHasMore || !hiddenGearNextCursor}
              >
                Next 500
              </button>
              <span style={{ fontSize: '0.9em', color: '#666' }}>Page {hiddenGearPageIndex}</span>
            </div>
            <ul className={styles.list} style={{ marginTop: 20 }}>
              {hiddenGear.map(item => (
                <li key={item.id} className={`${styles.listItem} ${styles.gearListItem}`}>
                  <div className={styles.gearRow}>
                    <div className={styles.gearThumbWrap}>
                      {renderGearThumb(item)}
                    </div>
                    <div className={styles.gearRowMain}>
                      <strong>{formatAdminProductLabel(item)}</strong>
                    </div>
                  </div>
                  <button onClick={() => handleToggleHide(item.id, false)}>Unhide</button>
                </li>
              ))}
              {hiddenGear.length === 0 && <p>No hidden items.</p>}
            </ul>
          </section>
        )}

        {adminSection === 'new-gear' && (
          <section className={styles.section}>
            <h2>Gear Intake & Search</h2>
            <p style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '0.9em' }}>
              {gearSearchQuery.trim()
                ? `Search loaded: ${newGearSearchLoadedCount || newGear.length} | On this page: ${newGear.length}`
                : `Found items: ${newGear.length}`}
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <input 
                type="text" 
                placeholder="Search raw gear feed..." 
                value={gearSearchQuery}
                onChange={e => setGearSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchGear()}
                style={{ padding: 8, flex: 1, border: '1px solid #ccc', borderRadius: 4 }}
              />
              <button onClick={handleSearchGear}>Search</button>
              <button onClick={fetchNewGear}>Refresh Recent</button>
            </div>

            {gearSearchQuery.trim() && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <button
                  onClick={() => handleSearchGearPage(1)}
                  disabled={newGearLoading || newGearSearchPage <= 1}
                >
                  Reset Search
                </button>
                <button
                  onClick={() => handleSearchGearPage(newGearSearchPage + 1)}
                  disabled={newGearLoading || !newGearSearchHasMore || !newGearSearchCursor}
                >
                  Load More 300
                </button>
                <span style={{ fontSize: '0.9em', color: '#666' }}>Page {newGearSearchPage}</span>
              </div>
            )}
            
            {newGearLoading && <p>Loading...</p>}
            {newGearError && <p className={styles.error}>{newGearError}</p>}
            
            {!newGearLoading && newGear.length > 0 ? (
              <ul className={styles.list}>
                {newGear.map((item, index) => (
                  <li key={item.id || index} className={`${styles.listItem} ${styles.gearListItem}`}>
                    <div className={styles.gearRow}>
                      <div className={styles.gearThumbWrap}>
                        {renderGearThumb(item)}
                      </div>
                      <div className={styles.gearRowMain}>
                        <div className={styles.mountainDetails} style={{ flex: 1 }}>
                          <span>
                            <strong>{formatAdminProductLabel(item)}</strong>
                             <div style={{ fontSize: '0.85em', color: '#fff', marginTop: 4 }}>
                               Category: {item.category || 'Uncategorized'} | {item.price}
                            </div>
                            {item.description && (
                              <div style={{ fontSize: '0.8em', color: '#888', marginTop: 4, maxHeight: 40, overflow: 'hidden' }}>
                                {item.description}
                              </div>
                            )}
                          </span>
                        </div>
                        <button 
                          style={{ background: '#007bff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', marginLeft: 10 }}
                          onClick={() => setValidatingItem(item)}
                        >
                          Validate
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : !newGearLoading && <p>No gear found.</p>}
          </section>
        )}

        {adminSection === 'legal' && (
          <section className={styles.section}>
            <h2>Legal Documents</h2>
            <p style={{ opacity: 0.7, marginBottom: '1rem' }}>Updating these will notify users of changes via the legal update popup.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div>
                <h3>Privacy Policy</h3>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label>Version:</label>
                    <input 
                      type="text" 
                      value={legalVersions.privacy} 
                      onChange={e => setLegalVersions(prev => ({ ...prev, privacy: e.target.value }))}
                      style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc', width: '100px' }}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>
                    <input 
                      type="checkbox" 
                      checked={materialChange.privacy}
                      onChange={e => setMaterialChange(prev => ({ ...prev, privacy: e.target.checked }))}
                    />
                    Material Change
                  </label>
                </div>
                <textarea 
                  style={{ width: '100%', minHeight: '300px', padding: '12px', fontFamily: 'monospace', borderRadius: '8px', border: '1px solid #ddd' }}
                  value={legalContent.privacy}
                  onChange={e => setLegalContent(prev => ({ ...prev, privacy: e.target.value }))}
                />
                <button onClick={() => handleSaveLegal('privacy')} disabled={legalLoading} style={{ marginTop: '12px' }}>
                  {legalLoading ? 'Saving...' : 'Update Privacy Policy'}
                </button>
              </div>

              <div style={{ borderTop: '1px solid #eee', paddingTop: '2rem' }}>
                <h3>Terms of Service</h3>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label>Version:</label>
                    <input 
                      type="text" 
                      value={legalVersions.terms} 
                      onChange={e => setLegalVersions(prev => ({ ...prev, terms: e.target.value }))}
                      style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc', width: '100px' }}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>
                    <input 
                      type="checkbox" 
                      checked={materialChange.terms}
                      onChange={e => setMaterialChange(prev => ({ ...prev, terms: e.target.checked }))}
                    />
                    Material Change
                  </label>
                </div>
                <textarea 
                  style={{ width: '100%', minHeight: '300px', padding: '12px', fontFamily: 'monospace', borderRadius: '8px', border: '1px solid #ddd' }}
                  value={legalContent.terms}
                  onChange={e => setLegalContent(prev => ({ ...prev, terms: e.target.value }))}
                />
                <button onClick={() => handleSaveLegal('terms')} disabled={legalLoading} style={{ marginTop: '12px' }}>
                  {legalLoading ? 'Saving...' : 'Update Terms of Service'}
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default AdminPage;